/**
 * Lumistelle Carousel Engine - Singleton Implementation
 * High-performance carousel system for multiple instances
 * Version: 4.0 - Production Ready (Unified)
 */
//console.log('[LumistelleCarousel] Engine file loaded');

(function(window, document) {
  'use strict';
 
  // ======================================================================
  // SINGLETON CAROUSEL ENGINE
  // ======================================================================
  class LumistelleCarouselEngine {
    constructor() {
      if (LumistelleCarouselEngine.instance) {
        return LumistelleCarouselEngine.instance;
      }

      this.instances = new Map();
      this.state = {
        instances: {},
        products: {},
        ui: { activeCarousel: null, touchStart: null, isDragging: false },
        cart: { item_count: 0, updating: false },
        wishlist: JSON.parse(localStorage.getItem('lumistelle_wishlist') || '[]')
      };

      // PHASE 6: WeakMap for element-based lookups (better GC)
      this.elementToInstance = new WeakMap();

      // PHASE 6: Track instance IDs separately (WeakMap doesn't support iteration)
      this.instanceIds = new Set();

      // Add state protection
      const protectedState = this.state;
      Object.defineProperty(this, 'state', {
        get() { return protectedState; },
        set(value) { 
          console.error('[LumistelleCarousel] Attempted to overwrite state!', value);
          return false;
        }
      });

      this.rafId = null;
      this.rafCallbacks = new Set();
      this.observers = new Map();
      this.templateCache = new Map();
      this.productPool = [];    
      this.poolSize = 100;
      this.variantData = new Map();

      // ======================================================================
      // PHASE 1: LIFECYCLE MANAGEMENT INFRASTRUCTURE
      // ======================================================================

      // Instance Activity Tracking
      this.activeInstances = new Set(); // IDs of currently active instances
      this.pendingCleanup = new Set(); // IDs scheduled for cleanup
      this.isPageVisible = true; // Track page visibility state

      // === PHASE 2: Scroll State Tracking ===
      this.isScrolling = false; // Track if user is actively scrolling
      this.scrollEndTimeout = null; // Debounce timer for scroll end detection
      this.scrollEndDelay = 150; // ms to wait after last scroll event before resuming timers
      this._scrollRafPending = false; // RAF gate to limit scroll handler to once per frame

      // === PHASE 3: Unified Visibility Observer Configuration ===
      this.unifiedObserverThreshold = 0.5; // Visibility threshold for autoplay/preload features
      this.visibleCards = new Map(); // instanceId -> Set of visible card elements

      // === PHASE 4: Card Element Cache ===
      // WeakMap ensures automatic cleanup when card elements are removed from DOM
      this.cardElementCache = new WeakMap();

      // ======================================================================
      // PHASE 3: EVENT LISTENER MANAGEMENT
      // ======================================================================

      // Bound Handler References (prevents .bind() memory leak)
      this.boundHandlers = {
        click: null,
        mousedown: null,
        touchstart: null,
        touchmove: null,
        touchend: null,
        resize: null,
        beforeunload: null,
        shopifySection: null
      };

      // Track if global listeners are attached
      this.globalListenersAttached = false;

      // Timer Registry - tracks all setTimeout/setInterval
      this.timers = new Map(); // instanceId -> Set of { id, type, created }
      this.globalTimers = new Set(); // Global timers not tied to instances

      // Event Listener Registry - tracks all addEventListener calls
      this.eventListeners = new Map(); // instanceId -> Array of { target, event, handler, options }
      this.globalEventListeners = []; // Global listeners not tied to instances

      // Enhanced Observer Registry (existing Map structure enhanced)
      // this.observers already exists, we'll enhance its usage in methods below

      this.config = {
        animationDuration: 300,
        swipeThreshold: 50,
        velocityThreshold: 0.3,
        productsPerView: { mobile: 1.2, tablet: 3, desktop: 4 },
        // Phase 2: Touch-optimized animation settings
        touchAnimationDuration: 120,  // Faster for direct manipulation feel
        touchAnimationEasing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' // Snappy ease-out
      };

      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchStartTime = 0;

      this.updateQueues = new Map();
      this.rafScheduled = false;

      this.hydrationQueue = new Map();
      this.debouncedProcessHydration = this.debounce(this._processHydrationQueue.bind(this), 150);

      this.init();
      LumistelleCarouselEngine.instance = this;
      this._setupPageVisibilityTracking();
    }

    // ======================================================================
    // INITIALIZATION & CORE SYSTEMS
    // ======================================================================
    init() {
      this.setupEventDelegation();
      this.setupAnimationLoop();
      this.createTemplates();
      this.initializeProductPool();

      // --- NEW: Self-contained Recently Viewed Tracking ---
      const pageType = document.body.dataset.pageType;
      if (pageType === 'product') {
        const productHandle = document.body.dataset.productHandle;
        if (productHandle) {
          this.trackRecentlyViewed(productHandle);
        }
      }
      // --- END NEW ---

      this.mark('engine:initialized');

      // PHASE 3: Setup cleanup on page unload
      this.setupUnloadHandler();

      // PHASE 3: Setup Shopify theme editor handlers
      this.setupShopifyEventHandlers();
    }

    // ======================================================================
    // PHASE 1: LIFECYCLE TRACKING METHODS
    // ======================================================================

    /**
     * Setup page visibility tracking to pause/resume activities
     * PHASE 2: Now controls animation loop based on visibility
     */
    _setupPageVisibilityTracking() {
      document.addEventListener('visibilitychange', () => {
        this.isPageVisible = !document.hidden;
        
        if (this.isPageVisible) {        
          // PHASE 2: Resume animation loop if there are active callbacks
          if (this.hasActiveCallbacks() && !this.rafIsRunning) {
            this.startAnimationLoop();
          }
        } else {         
          // PHASE 2: Stop animation loop to save CPU
          if (this.rafIsRunning) {
            this.stopAnimationLoop();
          }
        }
      });
    }

    // === PHASE 2: Scroll State Management ===

    /**
     * Handle scroll start - pause timer work
     * Called on every scroll event (passive, non-blocking)
     * 
     * === SCROLL PERF: RAF-throttled to eliminate timer churn ===
     * Previous approach: clearTimeout + setTimeout on EVERY scroll event (~60/sec)
     * New approach: RAF gate ensures we only process once per frame. A single
     * trailing setTimeout detects scroll-end — we only reset it when the RAF fires,
     * not on every raw scroll event. Reduces timer allocations from ~60/sec to ~1/sec.
     */
    _handleScrollEvent() {
      // Mark as scrolling immediately (cheap boolean flip, no allocation)
      if (!this.isScrolling) {
        this.isScrolling = true;
      }
      
      // RAF gate: only process one scroll event per animation frame
      if (this._scrollRafPending) return;
      this._scrollRafPending = true;
      
      requestAnimationFrame(() => {
        this._scrollRafPending = false;
        
        // Reset the scroll-end timeout (once per frame, not once per scroll event)
        if (this.scrollEndTimeout) {
          clearTimeout(this.scrollEndTimeout);
        }
        this.scrollEndTimeout = setTimeout(() => {
          this._handleScrollEnd();
        }, this.scrollEndDelay);
      });
    }

    /**
     * Handle scroll end - resume timer work
     * Called after scrollEndDelay ms of no scroll events
     */
    _handleScrollEnd() {
      this.isScrolling = false;
      this.scrollEndTimeout = null;
      // Optional: dispatch event for other modules to react
      // this.dispatchEvent('scroll:end', {});
    }

    /**
     * Check if scrolling is active (for use in timer callbacks)
     * @returns {boolean} True if user is currently scrolling
     */
    isUserScrolling() {
      return this.isScrolling;
    }

    /**
     * PHASE 3: Setup handler for page unload
     * Ensures cleanup happens when page is closed/navigated away
     */
    setupUnloadHandler() {
      this.boundHandlers.beforeunload = () => {
        this.cleanupGlobalListeners();
        this.stopAnimationLoop();
        // Full cleanup will be added in Phase 7
      };

      window.addEventListener('beforeunload', this.boundHandlers.beforeunload);
      this.registerEventListener('global', window, 'beforeunload', this.boundHandlers.beforeunload);
    }

    /**
     * PHASE 3: Setup handlers for Shopify theme editor events
     * Shopify's Online Store 2.0 uses AJAX to reload sections
     */
    setupShopifyEventHandlers() {
      // Handler for section unload (theme editor)
      this.boundHandlers.shopifySection = (event) => {
        const sectionId = event.detail?.sectionId;
        
        if (!sectionId) return;

        // Find and destroy any instances in this section
        this.instances.forEach((instance, instanceId) => {
          if (instanceId.includes(sectionId) || instance.element?.closest(`[data-section-id="${sectionId}"]`)) {
            this.destroyComplete(instanceId);
          }
        });
      };

      // Listen for Shopify section unload events
      document.addEventListener('shopify:section:unload', this.boundHandlers.shopifySection);
      this.registerEventListener('global', document, 'shopify:section:unload', this.boundHandlers.shopifySection);
    }

    /**
     * Track instance activity state
     * @param {string} instanceId - The instance identifier
     * @param {boolean} isActive - Whether instance is active
     */
    _trackInstanceActivity(instanceId, isActive) {
      if (isActive) {
        this.activeInstances.add(instanceId);
        this.pendingCleanup.delete(instanceId); // Cancel any pending cleanup
      } else {
        this.activeInstances.delete(instanceId);
      }
    }

    /**
     * Schedule cleanup for an instance with delay
     * @param {string} instanceId - The instance identifier  
     * @param {number} delayMs - Delay before cleanup in milliseconds
     */
    _scheduleCleanup(instanceId, delayMs = 100) {
      if (this.pendingCleanup.has(instanceId)) {
        return; // Already scheduled
      }
      
      this.pendingCleanup.add(instanceId);
      
      const timerId = setTimeout(() => {
        this._executeScheduledCleanup(instanceId);
      }, delayMs);
      
      // Store timer so it can be cancelled if needed
      if (!this.timers.has('__cleanup__')) {
        this.timers.set('__cleanup__', new Set());
      }
      this.timers.get('__cleanup__').add({ id: timerId, type: 'timeout', created: Date.now() });
    }

    /**
     * Execute scheduled cleanup for an instance
     * @param {string} instanceId - The instance identifier
     */
    _executeScheduledCleanup(instanceId) {
      if (!this.pendingCleanup.has(instanceId)) {
        return; // Cleanup was cancelled
      }
      
      this.pendingCleanup.delete(instanceId);
      
      // Check if instance still exists and should be cleaned
      if (this.instances.has(instanceId) && !this.activeInstances.has(instanceId)) {
        // Actual cleanup will be added in Phase 6
        // For now, just log that we would clean up
      }
    }

    /**
     * PHASE 5: Enhanced timer registration with element tracking
     * @param {string} instanceId - The carousel instance ID
     * @param {number} timerId - The timer ID from setInterval/setTimeout
     * @param {string} type - Timer type for debugging (autoplay, grid-autoplay, image-cycle, preload)
     * @param {HTMLElement} element - Optional: element this timer is attached to (for per-element timers)
     */
    registerTimer(instanceId, timerId, type = 'generic', element = null) {
      if (!this.timers.has(instanceId)) {
        this.timers.set(instanceId, new Set());
      }
      
      const timerData = {
        timerId,
        type,
        created: Date.now(),
        element: element || null // Store element reference for per-element timers
      };
      
      this.timers.get(instanceId).add(timerData);
            
      return timerData; // Return for potential future use
    }

    /**
     * PHASE 5 HOT FIX: Safe removal of timer from registry
     * Collects items to delete, then deletes them (avoids Set.forEach deletion issues)
     */
    removeTimerFromRegistry(instanceId, timerId, type) {
      const timersSet = this.timers.get(instanceId);
      if (!timersSet || timersSet.size === 0) {
        return false;
      }
      
      // CRITICAL: Collect items to delete first, THEN delete
      const toDelete = [];
      timersSet.forEach(timerData => {
        if (timerData.timerId === timerId && timerData.type === type) {
          toDelete.push(timerData);
        }
      });
      
      // Now safe to delete
      toDelete.forEach(item => {
        timersSet.delete(item);
        // Clear element reference to help GC
        if (item.element) {
          item.element = null;
        }
      });
      
      if (toDelete.length > 0) {
        return true;
      }
      
      return false;
    }

    /**
     * PHASE 5 HOT FIX: Enhanced timer clearing with safe deletion pattern
     */
    clearInstanceTimers(instanceId) {
      const timersSet = this.timers.get(instanceId);
      if (!timersSet || timersSet.size === 0) return 0;
      
      let cleared = 0;
      let elementTimersCleared = 0;
      
      // PHASE 5 HOT FIX: Convert Set to Array for safe iteration and deletion
      const timersArray = Array.from(timersSet);
      
      timersArray.forEach(timerData => {
        // Clear the timer itself
        clearInterval(timerData.timerId);
        clearTimeout(timerData.timerId); // Safe to call both
        cleared++;
        
        // PHASE 5: If this timer is attached to an element, clean up element reference
        if (timerData.element) {
          elementTimersCleared++;
          
          // Clear various element-level timer properties
          if (timerData.element.imageCycleTimer === timerData.timerId) {
            timerData.element.imageCycleTimer = null;
          }
          if (timerData.element.preloadTimer === timerData.timerId) {
            timerData.element.preloadTimer = null;
          }
          
          // Remove element reference to help GC
          timerData.element = null;
        }
      });
      
      // Now safe to delete the entire Set
      this.timers.delete(instanceId);
      return cleared;
    }

    /**
     * Clear all timers across all instances
     * @returns {number} Total number of timers cleared
     */
    clearAllTimers() {
      let totalCount = 0;
      
      this.timers.forEach((instanceTimers, instanceId) => {
        instanceTimers.forEach(timer => {
          if (timer.type === 'interval') {
            clearInterval(timer.id);
          } else {
            clearTimeout(timer.id);
          }
          totalCount++;
        });
      });
      
      this.timers.clear();
      return totalCount;
    }


    // ======================================================================
    // PHASE 1: OBSERVER REGISTRY METHODS
    // ======================================================================

    /**
     * Register an IntersectionObserver for tracking and cleanup
     * @param {string} key - Unique key for this observer
     * @param {IntersectionObserver} observer - The observer instance
     * @param {string} type - Type of observer (e.g., 'lazy', 'scroll', 'infinite')
     * @param {string} instanceId - The instance this observer belongs to
     * @returns {IntersectionObserver} The observer (for convenience)
     */
    registerObserver(key, observer, type = 'generic', instanceId = 'global') {
      if (this.observers.has(key)) {
        const existing = this.observers.get(key);
        if (existing.observer && typeof existing.observer.disconnect === 'function') {
          existing.observer.disconnect();
        }
      }
      
      this.observers.set(key, {
        observer: observer,
        type: type,
        instanceId: instanceId,
        created: Date.now()
      });
      
      return observer;
    }

    /**
     * Disconnect and remove all observers for a specific instance
     * @param {string} instanceId - The instance identifier
     * @returns {number} Number of observers disconnected
     */
    disconnectInstanceObservers(instanceId) {
      let count = 0;
      const keysToDelete = [];
      
      this.observers.forEach((data, key) => {
        if (data.instanceId === instanceId) {
          if (data.observer && typeof data.observer.disconnect === 'function') {
            data.observer.disconnect();
            count++;
          }
          keysToDelete.push(key);
        }
      });
      
      keysToDelete.forEach(key => this.observers.delete(key));
      
      if (count > 0) {
      }
      return count;
    }

    /**
     * Disconnect and remove all observers
     * @returns {number} Total number of observers disconnected
     */
    disconnectAllObservers() {
      let count = 0;
      
      this.observers.forEach((data, key) => {
        if (data.observer && typeof data.observer.disconnect === 'function') {
          data.observer.disconnect();
          count++;
        }
      });
      
      this.observers.clear();
      return count;
    }

    // ======================================================================
    // PHASE 1: EVENT LISTENER REGISTRY METHODS
    // ======================================================================

    /**
     * Register an event listener for tracking and cleanup
     * @param {string} instanceId - The instance identifier (or 'global')
     * @param {EventTarget} target - The event target (element, document, window)
     * @param {string} event - The event name
     * @param {Function} handler - The event handler function
     * @param {Object} options - Event listener options
     */
    registerEventListener(instanceId, target, event, handler, options = {}) {
      if (!instanceId) {
        instanceId = 'global';
      }
      
      if (!this.eventListeners.has(instanceId)) {
        this.eventListeners.set(instanceId, []);
      }
      
      this.eventListeners.get(instanceId).push({
        target: target,
        event: event,
        handler: handler,
        options: options,
        registered: Date.now()
      });
    }

    /**
     * Remove all event listeners for a specific instance
     * @param {string} instanceId - The instance identifier
     * @returns {number} Number of listeners removed
     */
    removeInstanceEventListeners(instanceId) {
      const instanceListeners = this.eventListeners.get(instanceId);
      if (!instanceListeners) {
        return 0;
      }
      
      let count = 0;
      instanceListeners.forEach(listener => {
        try {
          listener.target.removeEventListener(listener.event, listener.handler, listener.options);
          count++;
        } catch (error) {
        }
      });
      
      this.eventListeners.delete(instanceId);
      return count;
    }

    /**
     * Remove all event listeners across all instances
     * @returns {number} Total number of listeners removed
     */
    removeAllEventListeners() {
      let totalCount = 0;
      
      this.eventListeners.forEach((instanceListeners, instanceId) => {
        instanceListeners.forEach(listener => {
          try {
            listener.target.removeEventListener(listener.event, listener.handler, listener.options);
            totalCount++;
          } catch (error) {
          }
        });
      });
      
      this.eventListeners.clear();
      return totalCount;
    }

    // ======================================================================
    // STATE MANAGEMENT & PUB/SUB
    // ======================================================================
    getState(path = null) {
      if (!path) return JSON.parse(JSON.stringify(this.state));
      return path.split('.').reduce((o, k) => (o || {})[k], this.state);
    }

    setState(path, value) {
      const props = path.split('.');
      let current = this.state;
      for (let i = 0; i < props.length - 1; i++) {
        current = current[props[i]];
      }
      current[props[props.length - 1]] = value;

      if (path === 'wishlist') {
        localStorage.setItem('lumistelle_wishlist', JSON.stringify(value));
      }

      this.dispatchEvent(path.replace(/\./g, ':') + ':updated', value);
    }

    dispatchEvent(topic, detail) {
      const event = new CustomEvent(`lumistelle:${topic}`, { detail, bubbles: true, cancelable: true });
      document.dispatchEvent(event);
    }

    // ======================================================================
    // UTILITIES
    // ======================================================================
    ajax = {
      postJSON: async (url, data) => {
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(data)
          });
          if (!response.ok) {
            const errorJson = await response.json().catch(() => null);
            throw new Error(errorJson?.description || `HTTP error ${response.status}`);
          }
          return await response.json();
        } catch (error) {
          throw error;
        }
      },
      addToCart: async (variantId, quantity = 1) => {
        return this.ajax.postJSON('/cart/add.js', { items: [{ id: variantId, quantity }] });
      }
    };

    batchDOMOperations(operations) {
      return new Promise(resolve => {
        requestAnimationFrame(() => {
          let measurements = {};
          
          // Read phase
          if (operations.read) {
            if (typeof operations.read === 'function') {
              measurements = operations.read();
            } else {
              for (const key in operations.read) {
                measurements[key] = operations.read[key]();
              }
            }
          }
          
          // Write phase
          if (operations.write) {
            if (typeof operations.write === 'function') {
              operations.write(measurements);
            } else if (Array.isArray(operations.write)) {
              for (const writeFn of operations.write) {
                writeFn(measurements);
              }
            }
          }
          
          resolve(measurements);
        });
      });
    }

    formatMoney(cents, hideDecimals) {
      if (typeof cents !== 'number') return hideDecimals ? '$0' : '$0.00';
      return hideDecimals ? `$${Math.round(cents / 100)}` : `$${(cents / 100).toFixed(2)}`;
    }

    debounce(func, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    }

    _sanitizePrice(price) {
      // Handles null, undefined, or empty strings gracefully.
      if (!price) return 0;

      // Convert input to a number. Handles strings like "34.65" and numbers like 34.65.
      const numericPrice = parseFloat(price);
      if (isNaN(numericPrice)) return 0;

      // Check if the number is likely already in cents.
      // A price greater than 200 (i.e., 20000 cents) is a safe threshold.
      // The `numericPrice % 1 !== 0` check handles floats (e.g., 34.65).
      if (numericPrice > 20000 || numericPrice % 1 !== 0) {
        // Value is either a large integer (already in cents) or a float (in dollars).
        // If it's a float, it must be converted.
        if (numericPrice % 1 !== 0) {
          return Math.round(numericPrice * 100);
        }
        // Otherwise, it's a large integer, so it's already in cents.
        return numericPrice;
      }
      
      // If we are here, it's a small integer, which is ambiguous.
      // However, every API providing dollars gives a float or string float.
      // Only Liquid/Recommendations give integer cents.
      // Therefore, the most reliable assumption for a small integer is that it is already in cents.
      // The only edge case is a product with a flat dollar price like `$5.00`.
      // The previous logic handles this correctly by checking for floats.
      // Let's refine: if it has decimals, it's dollars. If not, it's cents.
      
      if (String(price).includes('.')) {
          // It's a float or string float like 34.65 or "34.65", so it's in dollars.
          return Math.round(numericPrice * 100);
      }

      // It's an integer like 3465 or "3465", so it's already in cents.
      return numericPrice;
    }

    _createMediaToColorMap(productData) {
      const map = {};
      const colorOptionKey = 'option1';

      const featuredMediaToColor = {};
      productData.variants.forEach(variant => {
        // --- START: THE DEFINITIVE FIX ---
        // This robustly checks for the image ID from BOTH possible data structures.
        let imageId = null;
        if (variant.featured_image_id) {
          // Path 1: For Liquid-rendered data
          imageId = variant.featured_image_id;
        } else if (variant.featured_image && variant.featured_image.id) {
          // Path 2: For API-rendered data (from homepage "Load More")
          imageId = variant.featured_image.id;
        }
        // --- END: THE DEFINITIVE FIX ---

        if (imageId) {
          const color = variant[colorOptionKey];
          if (color && !featuredMediaToColor[imageId]) {
              featuredMediaToColor[imageId] = color;
          }
        }
      });

      let currentColor = null;
      // Ensure productData.images exists before trying to loop
      if (productData.images && Array.isArray(productData.images)) {
        productData.images.forEach(image => {
          if (featuredMediaToColor[image.id]) {
            currentColor = featuredMediaToColor[image.id];
          }
          if (currentColor) {
            map[image.id] = currentColor;
          }
        });
      }

      return map;
    }

    // ======================================================================
    // INSTANCE MANAGEMENT
    // ======================================================================
    createInstance(element, config = {}) {
      const id = element.id || `lumistelle-${Date.now()}`;
      if (this.instances.has(id)) {
        return this.instances.get(id);
      }

      // Determine if it's a grid or carousel
      const isGrid = element.classList.contains('lumistelle-grid-section');

      const instance = new CarouselInstance(id, element, config, this);
      this.instances.set(id, instance);

      // PHASE 6: Store element → instance mapping for cleanup
      this.elementToInstance.set(element, instance);

      // PHASE 6: Track active instance ID
      this.instanceIds.add(id);

      this.state.instances[id] = { 
        isGrid: isGrid,
        currentIndex: 0, 
        isAnimating: false, 
        products: [], 
        initialized: false,
        dataLoaded: false
      };

      if (config.lazyInit !== false && !isGrid) { // Grids should initialize immediately
        this.setupLazyInit(instance);
      } else {
        this.initializeCarousel(instance);
      }
      return instance;
    }

    setupLazyInit(instance) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !this.state.instances[instance.id].initialized) {
              this.initializeCarousel(instance);
              observer.unobserve(entry.target);
            }
          });
        }, { rootMargin: '200px' }
      );
      observer.observe(instance.element);
      
      // PHASE 1: Use new registry system
      this.registerObserver(`lazy-${instance.id}`, observer, 'lazy', instance.id);
    }

    async initializeCarousel(instance) {
      try {
        const isGrid = this.state.instances[instance.id]?.isGrid;
        //console.log(`[Lumistelle] Initializing ${isGrid ? 'Grid' : 'Carousel'}: ${instance.id}`);
        this.mark(`${instance.id}:init:start`);

        if (this.state.instances[instance.id]?.initialized) return;
        this.state.instances[instance.id].initializing = true;

        // === LITE MODE: SSR cards are the source of truth ===
        // No data island exists on lite surfaces — polling for it would
        // spin 10s and reject, and the catch would wipe the SSR grid.
        const liteMode = instance.element.dataset.cardMode === 'lite';
        if (liteMode) {
          this.state.instances[instance.id].products = [];
          this.state.instances[instance.id].dataLoaded = true;
        } else {
          await this.loadProducts(instance);
        }

        const loadingEl = instance.element.querySelector('.lumistelle-carousel__loading');
        if (loadingEl) loadingEl.style.display = 'none';

        const stateProducts = this.state.instances[instance.id]?.products;
        if (!liteMode && (!stateProducts || stateProducts.length === 0)) {
          const sectionElement = instance.element.closest('.shopify-section');
          if (sectionElement) {
            sectionElement.style.display = 'none';
          }
          // Fallback in case it can't find the section, just empty the element
          instance.element.innerHTML = '';
          this.state.instances[instance.id].initialized = true;
          return;
        }

        if (isGrid) {
          this.setupGridDOM(instance);
        } else if (liteMode) {
          // CAROUSEL-LITE: cache the SSR scaffold, then build the stub
          // products array from the rendered cards so the slide layer
          // (bounds/dots/counter/autoplay) has a length to count off.
          this.setupCarouselDOMLite(instance);
          this._populateLiteStubProducts(instance);
        } else {
          this.setupCarouselDOM(instance);
        }
        
        if (liteMode) {
          // SSR cards stay as rendered — renderProducts would wipe the
          // track (innerHTML = '') and rebuild from (empty) island data.
          // Preserve hydrateCards' side effects only:
          document.dispatchEvent(new CustomEvent('lumistelle:cards:rendered'));
        } else {
          this.renderProducts(instance);
          this.hydrateCards(instance);
        }

        if (!isGrid) {
          this.setupNavigation(instance);
          this.updateLayout(instance);
        }

        if (isGrid && instance.loader) {
          this.state.instances[instance.id].currentPage = 1;
        if (instance.config.pagination_style === 'button') {
          this.setupLoadMoreButton(instance);
        } else { // Default to infinite scroll
          this.setupInfiniteScroll(instance);
        }
        }

        // Run image autoplay
        // === PHASE 3: Setup unified visibility observer FIRST ===
        // This must be called before feature-specific setup methods
        // as they now rely on the unified observer's visibility tracking
        this.setupUnifiedVisibilityObserver(instance);

        // Run image autoplay (now uses unified observer)
        if (isGrid) {
          this.setupGridImageAutoplay(instance);
        }

        // Setup Image Cycle Autoplay for Carousels (now uses unified observer)
        if (!isGrid && instance.config.imageAutoplay) {
          this.setupImageCycleAutoplay(instance);
        }

        // Setup Pagination for Grids
        if (isGrid) {
          if (instance.config.pagination_style === 'infinite') {
            this.setupInfiniteScrollAJAX(instance);
          } else {
            this.setupAjaxPagination(instance);
          }
        }

        this.state.instances[instance.id].initialized = true;
        this.state.instances[instance.id].initializing = false;

        if (window.LumistelleCarouselAnimations) {
          window.LumistelleCarouselAnimations.initialize(instance);
        }

        // PHASE 2: Mark instance as active now that it's initialized
        this._trackInstanceActivity(instance.id, true);

        // --- START: DEFINITIVE FIX ---
        // Defer one final layout update to ensure all elements are painted and sized correctly,
        // fixing any initial load miscalculations. This only applies to carousels.
        if (!isGrid) {
          requestAnimationFrame(() => {
            this.updateLayout(instance);
          });
        }
        // --- END: DEFINITIVE FIX ---

        this.dispatchEvent('section:initialized', { instance });
        this.mark(`${instance.id}:init:end`);
        //console.log(`[Lumistelle] Successfully initialized ${instance.id}`);

        instance.preloadState = new Map();
        instance.preloadTimer = null;
        this.setupPreloadingListeners(instance);

      } catch (error) {
        this.handleError('initializeCarousel', error);
        instance.element.innerHTML = '<div class="lumistelle-carousel__empty"><p>Error loading products.</p></div>';
        if (this.state.instances[instance.id]) {
          this.state.instances[instance.id].initialized = true;
        }
      }
    }

    /**
     * PHASE 6: Get instance with WeakMap fallback
     */
    getInstance(element) {
      // PHASE 6: Try direct WeakMap lookup first (fastest)
      const directInstance = this.elementToInstance.get(element);
      if (directInstance) return directInstance;
      
      // Fallback: traverse DOM to find container
      const container = element.closest('.lumistelle-carousel, .lumistelle-grid-section');
      if (!container) return null;
      
      // Try WeakMap on container
      const containerInstance = this.elementToInstance.get(container);
      if (containerInstance) return containerInstance;
      
      // Final fallback: ID lookup (backwards compatibility)
      return this.instances.get(container.id);
    }

    /**
     * PHASE 6: Get product data from element without storing on element
     */
    getProductDataFromElement(element) {
      const productId = parseInt(element.dataset.productId, 10);
      if (!productId) return null;
      
      return this.variantData.get(productId);
    }
        
    trackRecentlyViewed(productHandle) {
      if (!productHandle || !isNaN(productHandle)) {
          // Failsafe: Do not track if the handle is missing or is a number (likely an old ID).
          return;
      }
      try {
          let recentlyViewed = JSON.parse(localStorage.getItem('lumistelle_recently_viewed') || '[]');

          // --- SELF-HEALING FIX ---
          // 1. Filter out any old items that are purely numeric (i.e., old product IDs).
          // 2. Filter out any existing instance of the current product handle.
          let cleanedList = recentlyViewed
              .filter(item => typeof item === 'string' && isNaN(item))
              .filter(handle => handle !== productHandle);

          // Add the new valid handle to the beginning
          cleanedList.unshift(productHandle);

          // Limit the list to a reasonable size (e.g., 10)
          const limitedList = cleanedList.slice(0, 10);

          localStorage.setItem('lumistelle_recently_viewed', JSON.stringify(limitedList));
      } catch (error) {
          console.error('[Lumistelle] Error tracking recently viewed product:', error);
      }
  }

    // ======================================================================
    // PRODUCT LOADING (ROBUST & ASYNCHRONOUS)
    // ======================================================================
    async _fetchAndFormatProduct(handle) {
    try {
        const response = await fetch(`/products/${handle}.js`);
        if (!response.ok) return null;
        const product = await response.json();

        // --- START: DEFINITIVE FIX FOR .js ENDPOINT ---
        const firstVariant = product.variants[0] || {};
        // The .js endpoint uses 'featured_image' which can be a string URL or an object.
        // We prioritize the variant's image, then the product's main image, then the first from the media array.
        const featuredImageUrl = firstVariant.featured_image?.src || product.featured_image || (product.media.length > 0 ? product.media[0].src : null);
        
        if (!featuredImageUrl) {
            return null; // Skip products without any images
        }

        const createSwatchUrl = (src) => {
            if (!src) return '';
            try {
                const [base, params] = src.split('?');
                const pathParts = base.split('.');
                const extension = pathParts.pop();
                const pathWithoutExt = pathParts.join('.');
                return `${pathWithoutExt}_100x125_crop_top.${extension}${params ? '?' + params : ''}`;
            } catch (e) { return src; }
        };

        const imageIdToSrcMap = product.media.filter(m => m.media_type === 'image').reduce((acc, img) => {
          acc[img.id] = img.src;
          return acc;
        }, {});

        const colorToSwatchUrlMap = {};
        const colorsSeen = new Set();
        product.variants.forEach(variant => {
          const color = variant.option1;
          if (color && !colorsSeen.has(color) && variant.featured_media) {
            const imageSrc = imageIdToSrcMap[variant.featured_media.id];
            if (imageSrc) {
              colorToSwatchUrlMap[color] = createSwatchUrl(imageSrc);
              colorsSeen.add(color);
            }
          }
        });
        // --- END: DEFINITIVE FIX FOR .js ENDPOINT ---

        const adaptedProductData = {
          id: product.id,
          handle: product.handle,
          title: product.title,
          vendor: product.vendor,
          url: `/products/${product.handle}`,
          price: this._sanitizePrice(firstVariant.price),
          compare_at_price: this._sanitizePrice(firstVariant.compare_at_price),
          available: product.available,
          image: featuredImageUrl,
          srcset: `${featuredImageUrl}&width=300 300w, ${featuredImageUrl}&width=600 600w, ${featuredImageUrl}&width=900 900w, ${featuredImageUrl}&width=1200 1200w, ${featuredImageUrl}&width=1800 1800w, ${featuredImageUrl}&width=2400 2400w`,
          featured_image_id: firstVariant.featured_image?.id || null,
          inventory_quantity: firstVariant.inventory_quantity,
          tags: product.tags,
          description: product.description || '',
          is_new: false,
          is_bestseller: product.tags.includes('bestseller'),
          discount_percentage: firstVariant.compare_at_price > firstVariant.price ? Math.round((firstVariant.compare_at_price - firstVariant.price) * 100 / firstVariant.compare_at_price) : 0,
          rating: 0,
          reviews_count: 0,
          _needsRatingFetch: true,
          images: product.media.filter(m => m.media_type === 'image').map(img => ({
            id: img.id,
            src: `${img.src}&width=1200`,
            swatch_url: createSwatchUrl(img.src),
            srcset: `${img.src}&width=300 300w, ${img.src}&width=600 600w, ${img.src}&width=900 900w, ${img.src}&width=1200 1200w, ${img.src}&width=1800 1800w, ${img.src}&width=2400 2400w`
          })),
          variants: product.variants.map(v => ({
              ...v,
              price: this._sanitizePrice(v.price),
              compare_at_price: this._sanitizePrice(v.compare_at_price),
              // Ensure featured_image object exists for consistency
              featured_image: v.featured_image || null,
              featured_image_id: v.featured_media?.id || null
          })),
          colorToSwatchUrlMap: colorToSwatchUrlMap,
          options_with_values: product.options.map(opt => ({ name: opt.name, values: opt.values, position: opt.position }))
        };
        // Async-fetch the rating from Liquid metafields (not available in .js endpoint)
        const ratingData = await this._fetchProductRating(handle);
        adaptedProductData.rating = ratingData.rating;
        adaptedProductData.reviews_count = ratingData.reviews_count;
        delete adaptedProductData._needsRatingFetch;

        return adaptedProductData;
    } catch (error) {
        console.error(`[Lumistelle] Failed to fetch product data for handle: ${handle}`, error);
        return null;
    }
  }

  async _fetchProductRating(handle) {
      try {
        const resp = await fetch(`/products/${handle}?section_id=lumistelle-ratings-data`);
        if (!resp.ok) return { rating: 0, reviews_count: 0 };
        const html = await resp.text();
        const match = html.match(/<script[^>]*data-lumistelle-rating[^>]*>([\s\S]*?)<\/script>/);
        if (match && match[1]) {
          const data = JSON.parse(match[1].trim());
          return { rating: parseFloat(data.rating) || 0, reviews_count: parseInt(data.reviews_count, 10) || 0 };
        }
        return { rating: 0, reviews_count: 0 };
      } catch (e) {
        return { rating: 0, reviews_count: 0 };
      }
    }

    async _hydrateLoadMoreRatings(cardElements, cardDataArray, config) {
      if (!config.showRating) return;
      try {
        const ratingPromises = cardDataArray.map(p => this._fetchProductRating(p.handle));
        const ratings = await Promise.all(ratingPromises);
        ratings.forEach((rd, idx) => {
          cardDataArray[idx].rating = rd.rating;
          cardDataArray[idx].reviews_count = rd.reviews_count;
          // Update the variantData store so variant changes preserve the rating
          const stored = this.variantData.get(cardDataArray[idx].id);
          if (stored) {
            stored.rating = rd.rating;
            stored.reviews_count = rd.reviews_count;
          }
          // Re-render the rating display on the card
          this.updateRating(cardElements[idx], cardDataArray[idx]);
        });
      } catch (e) {
        // Ratings are non-critical — fail silently
      }
    }

    _loadProductsFromLiquid(instance) {
        return new Promise((resolve, reject) => {
            const instanceId = instance.id;
            const maxRetries = 100;
            let retries = 0;

            const checkData = () => {
                const dataKey = instance.element.id || instanceId;
                const carouselData = window.lumistelleCarouselData?.[dataKey];

                if (carouselData?.dataReady && carouselData?.products && Array.isArray(carouselData.products)) {
                    const validProducts = carouselData.products.filter(p => p && p.id);
                    resolve(validProducts);
                } else if (retries < maxRetries) {
                    retries++;
                    setTimeout(checkData, 100);
                } else {
                    console.error(`[LumistelleCarousel] FAILED to load Liquid data for instance ${instanceId}`);
                    reject(new Error(`Failed to load products for ${instanceId}`));
                }
            };
            checkData();
        });
    }

    async _loadProductsFromRecommendations(instance) {
    const productId = instance.element.dataset.productId;
    const limit = instance.config.maxProducts || 10;
    if (!productId) {
        console.error('[Lumistelle] No product ID found for recommendations carousel.');
        return [];
    }

    try {
        const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=${limit}`);
        if (!response.ok) throw new Error(`HTTP error ${response.status}`);
        const data = await response.json();
        
        if (!data.products || data.products.length === 0) return [];

        const adaptedProducts = data.products.map(product => {
            const firstVariant = product.variants[0] || {};
            const featuredImage = product.media.find(m => m.id === firstVariant.featured_media?.id) || product.media.find(m => m.media_type === 'image') || { src: '' };

            // --- START: PRICE STANDARDIZATION FIX ---
            // The Recommendations API returns prices as integers in cents. We ensure they are treated as numbers.
            const priceInCents = Number(firstVariant.price || 0);
            const compareAtPriceInCents = Number(firstVariant.compare_at_price || 0);
            // --- END: PRICE STANDARDIZATION FIX ---

            return {
                id: product.id,
                handle: product.handle,
                title: product.title,
                vendor: product.vendor,
                url: product.url,
                price: this._sanitizePrice(firstVariant.price),
                compare_at_price: this._sanitizePrice(firstVariant.compare_at_price),
                available: product.available,
                image: `${featuredImage.src}&width=1200`,
                srcset: `${featuredImage.src}&width=300 300w, ${featuredImage.src}&width=600 600w, ${featuredImage.src}&width=900 900w, ${featuredImage.src}&width=1200 1200w, ${featuredImage.src}&width=1800 1800w, ${featuredImage.src}&width=2400 2400w`,
                featured_image_id: featuredImage.id,
                inventory_quantity: firstVariant.inventory_quantity || 0,
                tags: product.tags,
                description: product.body_html || '',
                is_new: false,
                is_bestseller: product.tags.includes('bestseller'),
                discount_percentage: compareAtPriceInCents > priceInCents ? Math.round((compareAtPriceInCents - priceInCents) * 100 / compareAtPriceInCents) : 0,
                rating: 0,
                reviews_count: 0,
                images: product.media.filter(m => m.media_type === 'image').map(img => ({
                  id: img.id,
                  src: `${img.src}&width=1200`,
                  swatch_url: `${img.src}&width=100&height=125&crop=top`,
                  srcset: `${img.src}&width=300 300w, ${img.src}&width=600 600w, ${img.src}&width=900 900w, ${img.src}&width=1200 1200w, ${img.src}&width=1800 1800w, ${img.src}&width=2400 2400w`
                })),
                variants: product.variants.map(v => ({
                    ...v,
                    price: this._sanitizePrice(v.price),
                    compare_at_price: this._sanitizePrice(v.compare_at_price),
                    featured_image_id: v.featured_media?.id || null
                })),
                options_with_values: product.options.map(opt => ({ name: opt.name, values: opt.values, position: opt.position }))
            };
        });

        // Fetch ratings in parallel for all recommended products
        const ratingPromises = adaptedProducts.map(p => this._fetchProductRating(p.handle));
        const ratings = await Promise.all(ratingPromises);
        ratings.forEach((rd, idx) => {
          adaptedProducts[idx].rating = rd.rating;
          adaptedProducts[idx].reviews_count = rd.reviews_count;
        });

        return adaptedProducts;
    } catch (error) {
        console.error('[Lumistelle] Failed to fetch product recommendations:', error);
        return [];
    }
  }

    async _loadProductsFromCrossSell(instance) {
        const handlesString = instance.element.dataset.crossSellHandles;
        if (!handlesString) return [];
        const handles = handlesString.split(',').map(h => h.trim()).filter(Boolean);
        
        const productPromises = handles.map(handle => this._fetchAndFormatProduct(handle));
        const products = await Promise.all(productPromises);
        return products.filter(Boolean);
    }

    async _loadProductsFromRecentlyViewed(instance) {
        const currentProductHandle = instance.element.dataset.productHandle;
        const limit = instance.config.maxProducts || 10;
        try {
            const allHandles = JSON.parse(localStorage.getItem('lumistelle_recently_viewed') || '[]');
            const filteredHandles = allHandles.filter(handle => handle !== currentProductHandle).slice(0, limit);
            
            if (filteredHandles.length === 0) return [];

            const productPromises = filteredHandles.map(handle => this._fetchAndFormatProduct(handle));
            const products = await Promise.all(productPromises);
            return products.filter(Boolean);
        } catch (error) {
            console.error('[Lumistelle] Failed to load recently viewed products:', error);
            return [];
        }
    }

    async loadProducts(instance) {
      const instanceId = instance.id;
      const sourceType = instance.element.dataset.sourceType || 'liquid';
      let products = [];

      try {
        switch (sourceType) {
          case 'recommendations':
            //console.log(`[Lumistelle] Loading products for ${instanceId} from: Recommendations`);
            products = await this._loadProductsFromRecommendations(instance);
            break;
          case 'cross_sell':
            //console.log(`[Lumistelle] Loading products for ${instanceId} from: Cross-Sell`);
            products = await this._loadProductsFromCrossSell(instance);
            break;
          case 'recently_viewed':
            //console.log(`[Lumistelle] Loading products for ${instanceId} from: Recently Viewed`);
            products = await this._loadProductsFromRecentlyViewed(instance);
            break;
          default: // 'liquid'
            //console.log(`[Lumistelle] Loading products for ${instanceId} from: Liquid`);
            products = await this._loadProductsFromLiquid(instance);
            break;
        }

        const validProducts = products.filter(p => p && p.id);
        validProducts.forEach(p => this.variantData.set(p.id, p));

        if (this.state.instances[instanceId]) {
          this.state.instances[instanceId].products = validProducts;
          this.state.instances[instanceId].dataLoaded = true;
        }
        
        //console.log(`[LumistelleCarousel] Loaded ${validProducts.length} valid products for ${instanceId}`);
        return validProducts;

      } catch (error) {
        console.error(`[LumistelleCarousel] FAILED to load data for instance ${instanceId} (source: ${sourceType})`, error);
        if (this.state.instances[instanceId]) {
          this.state.instances[instanceId].products = [];
        }
        throw new Error(`Failed to load products for ${instanceId}`);
      }
    }

    // ======================================================================
    // DOM & RENDERING
    // ======================================================================
    setupCarouselDOM(instance) {
      instance.element.innerHTML = `
        <div class="lumistelle-carousel__container">
          <div class="lumistelle-carousel__viewport">
            <div class="lumistelle-carousel__track"></div>
            <div class="lumistelle-carousel__progress-wrapper">
              <div class="lumistelle-carousel__progress-fill"></div>
            </div>
          </div>
          <button class="lumistelle-carousel__arrow lumistelle-carousel__arrow--prev" aria-label="Previous">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2"/></svg>
          </button>
          <button class="lumistelle-carousel__arrow lumistelle-carousel__arrow--next" aria-label="Next">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M9 6L15 12L9 18" stroke="currentColor" stroke-width="2"/></svg>
          </button>
        </div>
        <div class="lumistelle-carousel__dots-wrapper">
          <div class="lumistelle-carousel__dots"></div>
          <div class="lumistelle-carousel__counter"></div>
        </div>
      `;
      
      // Cache elements
      instance.container = instance.element.querySelector('.lumistelle-carousel__container');
      instance.viewport = instance.element.querySelector('.lumistelle-carousel__viewport');
      instance.track = instance.element.querySelector('.lumistelle-carousel__track');
      instance.prevButton = instance.element.querySelector('.lumistelle-carousel__arrow--prev');
      instance.nextButton = instance.element.querySelector('.lumistelle-carousel__arrow--next');
      instance.dotsWrapper = instance.element.querySelector('.lumistelle-carousel__dots-wrapper');
      instance.dotsContainer = instance.element.querySelector('.lumistelle-carousel__dots');
      instance.counterElement = instance.element.querySelector('.lumistelle-carousel__counter');
      instance.progressBar = instance.element.querySelector('.lumistelle-carousel__progress-wrapper');
      instance.progressFill = instance.element.querySelector('.lumistelle-carousel__progress-fill');
    }
    
    setupGridDOM(instance) {
      // The grid's initial HTML is mostly in place from Liquid.
      // We just need to cache the container element for rendering products.
      instance.gridContainer = instance.element.querySelector('.lumistelle-grid__container');
      instance.loader = instance.element.querySelector('.lumistelle-grid__loader');
      // The grid doesn't use a track, so we render directly into the container.
      instance.track = instance.gridContainer; 
    }

    // === CAROUSEL-LITE: SSR scaffold is already in the DOM (viewport, track,
    // arrows, dots, progress rendered in Liquid). Mirror setupGridDOM: cache
    // references only, never innerHTML-rebuild (that would wipe the SSR cards).
    // Used only when liteMode && !isGrid. The full setupCarouselDOM path
    // (old island carousels) is left completely intact.
    setupCarouselDOMLite(instance) {
      instance.container    = instance.element.querySelector('.lumistelle-carousel__container');
      instance.viewport     = instance.element.querySelector('.lumistelle-carousel__viewport');
      instance.track        = instance.element.querySelector('.lumistelle-carousel__track');
      instance.prevButton   = instance.element.querySelector('.lumistelle-carousel__arrow--prev');
      instance.nextButton   = instance.element.querySelector('.lumistelle-carousel__arrow--next');
      instance.dotsWrapper  = instance.element.querySelector('.lumistelle-carousel__dots-wrapper');
      instance.dotsContainer= instance.element.querySelector('.lumistelle-carousel__dots');
      instance.counterElement = instance.element.querySelector('.lumistelle-carousel__counter');
      instance.progressBar  = instance.element.querySelector('.lumistelle-carousel__progress-wrapper');
      instance.progressFill = instance.element.querySelector('.lumistelle-carousel__progress-fill');
    }

    // === CAROUSEL-LITE: the slide layer counts everything off
    // state.products.length (~15 sites: bounds, dots, counter, autoplay,
    // drag-clamp). Lite has no island, so populate a lightweight STUB array —
    // one entry per SSR card, carrying only the id the slide layer reads —
    // straight from the rendered track children. Every existing length
    // calculation then works with zero edits to those 15 sites.
    _populateLiteStubProducts(instance) {
      const state = this.state.instances[instance.id];
      if (!state) return;
      const cards = instance.track
        ? instance.track.querySelectorAll('.lumistelle-carousel__product')
        : [];
      state.products = Array.from(cards).map(card => ({
        id: parseInt(card.dataset.productId, 10) || card.dataset.productId || null
      }));
      state.dataLoaded = true;
    }

    async loadMore(instance) {
      const state = this.state.instances[instance.id];
      if (state.isLoading || state.allProductsLoaded) return;

      const button = instance.element.querySelector('.lumistelle-grid__load-more-button');
      if (button) {
        button.disabled = true;
        button.classList.add('is-loading');
      }

      state.isLoading = true;
      const infiniteSpinner = instance.loader.querySelector('.lumistelle-grid__spinner');
      if (infiniteSpinner) infiniteSpinner.hidden = false;

      const isMobile = window.innerWidth < 750;
        let productsToLoad = isMobile ? instance.config.products_per_page_mobile : this.getProductsPerView(instance) * 2;
        productsToLoad = Math.floor(productsToLoad);
        
        const nextPage = (state.currentPage || 1) + 1;
        const url = `/collections/${instance.config.collection}/products.json?limit=${productsToLoad}&page=${nextPage}`;

        try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error ${response.status}`);
        const data = await response.json();

        if (data.products && data.products.length > 0) {
          const fragment = document.createDocumentFragment();
          const newCardElements = [];

          const createSwatchUrl = (src) => {
            if (!src) return '';
            try {
              const [base, params] = src.split('?');
              const pathParts = base.split('.');
              const extension = pathParts.pop();
              const pathWithoutExt = pathParts.join('.');
              return `${pathWithoutExt}_100x125_crop_top.${extension}${params ? '?' + params : ''}`;
            } catch (e) { return src; }
          };

          // PHASE 6 HOT FIX: Track product data separately for hydration
          const newCardData = [];

          for (const product of data.products) {
            const imageIdToSrcMap = product.images.reduce((acc, img) => {
              acc[img.id] = img.src;
              return acc;
            }, {});

            const colorToSwatchUrlMap = {};
            const colorsSeen = new Set();
            product.variants.forEach(variant => {
              const color = variant.option1;
              if (color && !colorsSeen.has(color) && variant.image_id) {
                const imageSrc = imageIdToSrcMap[variant.image_id];
                if (imageSrc) {
                  colorToSwatchUrlMap[color] = createSwatchUrl(imageSrc);
                  colorsSeen.add(color);
                }
              }
            });

            const firstVariant = product.variants[0] || {};
            const firstImage = product.images.find(img => img.id === firstVariant.image_id) || product.images[0] || {};
            
            const adaptedProductData = {
              id: product.id,
              handle: product.handle,
              title: product.title,
              vendor: product.vendor,
              url: `/products/${product.handle}`,
              price: this._sanitizePrice(firstVariant.price),
              compare_at_price: this._sanitizePrice(firstVariant.compare_at_price),
              available: product.variants.some(v => v.available),
              image: firstImage.src,
              srcset: `${firstImage.src}&width=300 300w, ${firstImage.src}&width=600 600w, ${firstImage.src}&width=900 900w, ${firstImage.src}&width=1200 1200w, ${firstImage.src}&width=1800 1800w, ${firstImage.src}&width=2400 2400w`,
              featured_image_id: firstImage.id,
              inventory_quantity: firstVariant.inventory_quantity,
              tags: product.tags,
              description: product.body_html || '',
              is_new: false,
              is_bestseller: product.tags.includes('bestseller'),
              discount_percentage: firstVariant.compare_at_price > firstVariant.price ? Math.round((parseFloat(firstVariant.compare_at_price) - parseFloat(firstVariant.price)) * 100 / parseFloat(firstVariant.compare_at_price)) : 0,
              rating: 0,
              reviews_count: 0,
              images: product.images.map(img => ({
                id: img.id,
                src: `${img.src}&width=1200`,
                swatch_url: createSwatchUrl(img.src),
                srcset: `${img.src}&width=300 300w, ${img.src}&width=600 600w, ${img.src}&width=900 900w, ${img.src}&width=1200 1200w, ${img.src}&width=1800 1800w, ${img.src}&width=2400 2400w`
              })),
              // --- START: THE DEFINITIVE FIX ---
              // Map over variants and convert each price to integer cents.
              variants: product.variants.map(v => ({
                ...v,
                price: this._sanitizePrice(v.price),
                compare_at_price: this._sanitizePrice(v.compare_at_price)
              })),
              // --- END: THE DEFINITIVE FIX ---
              colorToSwatchUrlMap: colorToSwatchUrlMap,
              options_with_values: product.options
            };
            this.variantData.set(adaptedProductData.id, adaptedProductData);
  
              let cardElement = this.productPool.pop() || this.templateCache.get('product').cloneNode(true);
              cardElement.dataset.productId = adaptedProductData.id;
              // PHASE 6: Don't store productData on element
              fragment.appendChild(cardElement);
              
              // PHASE 6 HOT FIX: Store element + data pairing for hydration
              newCardElements.push(cardElement);
              newCardData.push(adaptedProductData);
            }

            instance.track.appendChild(fragment);
          
          // PHASE 6 HOT FIX: Hydrate with tracked data instead of element property
          newCardElements.forEach((cardEl, index) => {
            const productData = newCardData[index];
            this.updateProductElement(cardEl, productData, index, instance.config);
            cardEl.setAttribute('data-hydrated', 'true');
          });;
          
          this.debouncedProcessHydration();

          // Async-hydrate ratings for Load More products (doesn't block render)
          this._hydrateLoadMoreRatings(newCardElements, newCardData, instance.config);

          if (window.LumistelleCarouselAnimations && typeof window.LumistelleCarouselAnimations.observeNewProducts === 'function') {
            window.LumistelleCarouselAnimations.observeNewProducts(instance, newCardElements);
          }

        // === PHASE 3: Add new products to unified visibility observer ===
        // This handles grid autoplay visibility tracking and mobile preloading
        this.observeNewCardsWithUnifiedObserver(instance, newCardElements);

        document.dispatchEvent(new CustomEvent('lumistelle:cards:rendered'));

          state.currentPage = nextPage;

          // A short page (fewer than requested) means this was the last page.
          // Retire the loader now instead of waiting for a click that fetches
          // an empty next page — fixes the button lingering after the final page.
          if (data.products.length < productsToLoad) {
            state.allProductsLoaded = true;
            if (instance.loader) instance.loader.remove();
          }
        } else {
          state.allProductsLoaded = true;
          if (instance.loader) instance.loader.remove();
        }

      } catch (error) {
        console.error('Failed to load more products:', error);
        if (instance.loader) instance.loader.remove();
      } finally {
        state.isLoading = false;
        if (instance.loader && !state.allProductsLoaded) {
          const infiniteSpinner = instance.loader.querySelector('.lumistelle-grid__spinner');
          if (infiniteSpinner) infiniteSpinner.hidden = true;
          
          if (button) {
            button.disabled = false;
            button.classList.remove('is-loading');
          }
        }
      }
    }

    setupInfiniteScroll(instance) {
      // PHASE 4: Prevent duplicate observer setup
      const observerKey = `infinite-scroll-${instance.id}`;
      if (this.observers.has(observerKey)) {
        return;
      }

      // Preload buffer so the fetch fires before the user hits the bottom.
      // (Previously referenced an undefined rootMarginPx — threw and aborted init.)
      const isMobile = window.innerWidth < 750;
      const preloadCount = isMobile ? 3 : 2;
      const estimatedCardHeight = isMobile ? window.innerHeight * 0.8 : 500;
      const rootMarginPx = Math.round(preloadCount * estimatedCardHeight);

      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this.loadMore(instance);
        }
      }, { rootMargin: `${rootMarginPx}px` });
      
      observer.observe(instance.loader);
      
      // PHASE 4: Register observer for proper cleanup
      this.registerObserver(observerKey, observer, 'infinite-scroll', instance.id);     
    }

    setupLoadMoreButton(instance) {
      const button = instance.element.querySelector('.lumistelle-grid__load-more-button');
      if (!button) return;

      const clickHandler = () => {
        this.loadMore(instance);
      };

      button.addEventListener('click', clickHandler);
      this.registerEventListener(instance.id, button, 'click', clickHandler);
    }

    // ======================================================================
    // INFINITE SCROLL FOR COLLECTION & SEARCH GRIDS (AJAX-BASED)
    // ======================================================================
    // Uses the existing loadMoreProductsAJAX() pipeline, triggered by
    // IntersectionObserver instead of click. This preserves filters, sort,
    // search queries, and server-rendered product cards.
    // ======================================================================

    setupInfiniteScrollAJAX(instance) {
      instance.paginationContainer = document.getElementById(
        `lumistelle-ajax-pagination-${instance.element.dataset.sectionId}`
      );
      instance.gridContainer = instance.element.querySelector('.lumistelle-grid__container');

      // If no pagination container exists, all products fit on one page — nothing to do
      if (!instance.paginationContainer || !instance.gridContainer) return;

      const observerKey = `infinite-scroll-ajax-${instance.id}`;
      if (this.observers.has(observerKey)) {
        return;
      }

      // Calculate a generous rootMargin so the fetch triggers well before the user
      // reaches the bottom: about 5 screens on phones (QA, October 2026). A fast
      // fling covered the old 2.4-screen head start before the next batch arrived.
      const isMobile = window.innerWidth < 750;
      const preloadCount = isMobile ? 6 : 3;
      const estimatedCardHeight = isMobile
        ? window.innerHeight * 0.8   // matches ~80vh mobile card height
        : 500;                        // conservative desktop estimate
      const rootMarginPx = Math.round(preloadCount * estimatedCardHeight);

      const engine = this;

      const observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting || instance.isLoading) return;

        // Find the next-page URL from the trigger element inside the container
        const trigger = instance.paginationContainer.querySelector('[data-next-url]');
        if (!trigger) return;

        const nextUrl = trigger.dataset.nextUrl;
        if (!nextUrl) return;

        // Use the existing AJAX pipeline — preserves filters, sort, search params
        engine.loadMoreProductsAJAX(instance, nextUrl).then(() => {
          // After innerHTML replacement, force the observer to re-evaluate.
          // Without this, if the container stays in the viewport (small batch),
          // the observer won't fire again because intersection state didn't change.
          if (instance.paginationContainer && instance.paginationContainer.isConnected) {
            observer.unobserve(instance.paginationContainer);
            observer.observe(instance.paginationContainer);
          }
        }).catch(() => {
          // loadMoreProductsAJAX handles its own errors — this is just a safety net
        });
      }, { rootMargin: `${rootMarginPx}px` });

      observer.observe(instance.paginationContainer);

      // Warm the next page in idle time (see _prefetchNextPage).
      this._prefetchNextPage(instance);

      // Register for proper cleanup on section destroy/unload
      this.registerObserver(observerKey, observer, 'infinite-scroll-ajax', instance.id);
    }

    setupGridImageAutoplay(instance) {
      if (!instance.config.enable_grid_image_autoplay) return;

      // PHASE 5 HOT FIX: Prevent duplicate timer setup
      if (instance.gridAutoplayTimer) {
        return;
      }

      // === PHASE 3: Use unified observer's visible cards tracking ===
      // The unified observer populates this.visibleCards.get(instance.id)
      // We just need to set up the timer and hover listeners
      
      // Ensure visible cards set exists (unified observer should have created it)
      if (!this.visibleCards.has(instance.id)) {
        this.visibleCards.set(instance.id, new Set());
      }
      
      const visibleCardsSet = this.visibleCards.get(instance.id);

      // Pause on hover logic
      const mouseEnterHandler = (e) => {
        const card = e.target.closest('.lumistelle-carousel__product');
        if (card) card.dataset.isHovered = 'true';
      };

      const mouseLeaveHandler = (e) => {
        const card = e.target.closest('.lumistelle-carousel__product');
        if (card) delete card.dataset.isHovered;
      };

      // Mouse only: a tap also fires mouseenter, and touch has nothing to end
      // that "hover", so a tapped card would stop cycling.
      if (window.matchMedia('(hover: hover)').matches) {
        instance.track.addEventListener('mouseenter', mouseEnterHandler, true);
        this.registerEventListener(instance.id, instance.track, 'mouseenter', mouseEnterHandler);

        instance.track.addEventListener('mouseleave', mouseLeaveHandler, true);
        this.registerEventListener(instance.id, instance.track, 'mouseleave', mouseLeaveHandler);
      }

      // Central timer - uses visibleCardsSet populated by unified observer
      instance.gridAutoplayTimer = setInterval(() => {
        // PHASE 2: Skip work if user is scrolling
        if (this.isScrolling) {
          return;
        }
        
        if (!this._photoCycleAllowed()) return;

        // Cards ripple in reading order and each advances only onto a photo
        // that is already loaded and decoded (see _rippleGridCards).
        this._rippleGridCards(instance, visibleCardsSet);
      }, instance.config.grid_autoplay_delay || 3000);

      // Register timer for tracking
      this.registerTimer(instance.id, instance.gridAutoplayTimer, 'grid-autoplay');
    }

    /**
     * PHOTO AUTOPLAY (grids and carousels): timed-cycle rules. Never on a
     * hidden tab, with reduced motion, or with Save-Data.
     * kind 'strip' is the carousel strip sliding (_carouselAutoplayAllowed);
     * anything else is card photo cycling. Each has a performance tool switch
     * (window.LumistellePerf exists only for tagged testers).
     */
    _photoCycleAllowed(kind) {
      if (document.hidden) return false;
      const perf = window.LumistellePerf;
      if (perf && perf.off(kind === 'strip' ? 'carousel-autoplay' : 'photo-cycle')) return false;
      if (!this._reducedMotionQuery) {
        this._reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      }
      if (this._reducedMotionQuery.matches) return false;
      return !(navigator.connection && navigator.connection.saveData);
    }

    /**
     * GRID PHOTO AUTOPLAY: one beat ripples through the visible cards in
     * reading order, like home-browse: at most 160 ms between neighbours and
     * the whole ripple within a quarter of the beat. Each card's work lands
     * in its own short task instead of all cards in one frame. A card with
     * keyboard focus inside sits the beat out.
     */
    _rippleGridCards(instance, cardsSet) {
      const beat = instance.config.grid_autoplay_delay || 3000;
      let focusedCard = null;
      try {
        const active = document.activeElement;
        if (active && active.matches(':focus-visible')) {
          focusedCard = active.closest('.lumistelle-carousel__product');
        }
      } catch (e) { /* :focus-visible unsupported: no focus pause */ }

      const cards = Array.from(cardsSet).filter(card => !card.dataset.isHovered && card !== focusedCard);
      // DOM order is reading order (row by row).
      cards.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
      const step = cards.length > 1 ? Math.min(160, (beat * 0.25) / (cards.length - 1)) : 0;

      if (instance.rippleTimers) instance.rippleTimers.forEach(clearTimeout);
      instance.rippleTimers = cards.map((card, i) => setTimeout(() => {
        // Re-checked at the card's turn: a scroll, a swipe or leaving view cancels it.
        if (this.isScrolling || !cardsSet.has(card) || card.dataset.isHovered) return;
        this._advanceCardWhenReady(card);
      }, Math.round(step * i)));
    }

    /**
     * PHOTO AUTOPLAY: advance a card only when its next photo is loaded and
     * decoded; otherwise start that photo now and hold this one until the
     * next beat (home-browse does the same). After advancing, the photo
     * after that is fetched and decoded straight away, a beat ahead, so the
     * decode never lands on the beat.
     */
    _advanceCardWhenReady(card) {
      const images = this._getCardImages(card);
      if (!images || images.length <= 1) return;
      const track = card.querySelector('.lumistelle-image-track');
      if (!track) {
        this.changeImage(card, 1);
        return;
      }
      const next = ((card.currentImageIndex || 0) + 1) % images.length;
      const nextImg = this._warmCardPhoto(track, next);
      if (nextImg && !nextImg._lumReady) return; // still loading: hold this photo
      this.changeImage(card, 1);
      this._warmCardPhoto(track, (next + 1) % images.length);
    }

    /**
     * Load and decode one slide's photo ahead of time. decode() resolves once
     * the photo is downloaded and decoded; a failure fails open, so a broken
     * photo never freezes the card.
     */
    _warmCardPhoto(track, index) {
      const img = track.children[index] ? track.children[index].querySelector('img') : null;
      if (!img) return null;
      if (img.dataset.src) {
        img.src = img.dataset.src;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.removeAttribute('data-src');
        img.removeAttribute('data-srcset');
      }
      if (!img._lumWarm) {
        const decoding = typeof img.decode === 'function' ? img.decode() : Promise.resolve();
        img._lumWarm = decoding.catch(() => {}).then(() => { img._lumReady = true; });
      }
      return img;
    }

    setupImageCycleAutoplay(instance) {
      if (!instance.config.imageAutoplay) return;

      // === PHASE 3: Unified observer handles visibility tracking ===
      // Timer start/stop is handled by _handleCardVisibilityChange
      // We just need to set up hover listeners here
      
      const products = instance.track.querySelectorAll('.lumistelle-carousel__product');
      const canHover = window.matchMedia('(hover: hover)').matches;
      products.forEach((card) => {
        // Register hover listeners for pause-on-hover functionality
        const mouseEnterHandler = () => {
          card.dataset.isHovered = 'true';
        };
        const mouseLeaveHandler = () => {
          delete card.dataset.isHovered;
        };

        // Mouse only (see setupGridImageAutoplay).
        if (!canHover) return;
        card.addEventListener('mouseenter', mouseEnterHandler);
        this.registerEventListener(instance.id, card, 'mouseenter', mouseEnterHandler);

        card.addEventListener('mouseleave', mouseLeaveHandler);
        this.registerEventListener(instance.id, card, 'mouseleave', mouseLeaveHandler);
      });
    }

    setupNavigation(instance) {
      const state = this.state.instances[instance.id];
      
      if (!state || !state.products || state.products.length === 0) {
        return;
      }
      
      // Setup desktop dots
      this.setupDots(instance);
      
      // Initialize progress bar explicitly
      if (instance.progressFill) {
        const productsPerView = Math.floor(this.getProductsPerView(instance));
        const totalSlides = Math.ceil(state.products.length / productsPerView);
        
        if (totalSlides > 1) {
          // Set initial progress (first slide)
          const initialProgress = (1 / totalSlides) * 100;
          instance.progressFill.style.width = `${initialProgress}%`;
          
          // Store total slides for later updates
          instance.totalSlidesMobile = totalSlides;
        } else {
          // Hide progress bar if only one slide
          instance.progressBar.style.display = 'none';
        }
      }
      
      // Update all navigation elements
      this.updateNavigation(instance);
      
      // Setup autoplay if configured
      if (instance.config.autoplay) {
        this.setupAutoplay(instance);
        this._watchCarouselAutoplay(instance);
      }
    }

    setupDots(instance) {
      const state = this.state.instances[instance.id];
      const productsPerView = Math.floor(this.getProductsPerView(instance));
      const totalSlides = Math.ceil(state.products.length / productsPerView);
      
      if (totalSlides <= 1 || !instance.dotsContainer) {
        if (instance.dotsWrapper) {
          instance.dotsWrapper.style.display = 'none';
        }
        return;
      }
      
      // Clear and create dots
      instance.dotsContainer.innerHTML = '';
      
      for (let i = 0; i < Math.min(totalSlides, 8); i++) {
        const dot = document.createElement('button');
        dot.className = 'lumistelle-carousel__dot';
        if (i === 0) dot.classList.add('active');
        dot.dataset.index = i;
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        instance.dotsContainer.appendChild(dot);
      }
      
      // Store total slides
      instance.totalSlides = totalSlides;
    }

    updateNavigation(instance) {
      const state = this.state.instances[instance.id];
      if (!state) return;
      
      const productsPerView = Math.floor(this.getProductsPerView(instance));
      const totalSlides = instance.totalSlides || Math.ceil(state.products.length / productsPerView);
      
      // Calculate current slide - special handling for last partial slide
      let currentSlide;
      const maxIndex = Math.max(0, state.products.length - productsPerView);
      
      if (state.currentIndex >= maxIndex && maxIndex > 0) {
        // We're at the last position, activate last dot
        currentSlide = totalSlides - 1;
      } else {
        // Normal calculation for other positions
        currentSlide = Math.floor(state.currentIndex / productsPerView);
      }
      
      // Update progress bar (mobile) - use stored mobile calculation
      if (instance.progressFill) {
        const mobileTotalSlides = instance.totalSlidesMobile || Math.ceil(state.products.length / this.getProductsPerView(instance));
        const mobileCurrentSlide = Math.floor(state.currentIndex / this.getProductsPerView(instance));
        const progressPercent = ((mobileCurrentSlide + 1) / mobileTotalSlides) * 100;
        
        // Force the width update
        instance.progressFill.style.width = `${Math.min(100, progressPercent)}%`;
      
      }
      
      // Update dots (desktop) - use the corrected currentSlide
      if (instance.dotsContainer) {
        const dots = instance.dotsContainer.querySelectorAll('.lumistelle-carousel__dot');
        dots.forEach((dot, index) => {
          dot.classList.toggle('active', index === currentSlide);
        });
      }
      
      // Update counter
      if (instance.counterElement) {
        const start = state.currentIndex + 1;
        const end = Math.min(state.currentIndex + productsPerView, state.products.length);
        instance.counterElement.textContent = `${start}-${end} of ${state.products.length} items`;
      }
      
      // Update navigation buttons
      const maxIndexNav = Math.max(0, state.products.length - productsPerView);
      instance.prevButton.disabled = state.currentIndex <= 0;
      instance.nextButton.disabled = state.currentIndex >= maxIndexNav;
    }

    setupAutoplay(instance) {
      // PHASE 5 HOT FIX #3: Check BOTH property AND registry for duplicates
      if (instance.autoplayInterval) {
        return;
      }
      
      // PHASE 5 HOT FIX #3: Also check if ANY autoplay timer exists in registry
      const timersSet = this.timers.get(instance.id);
      if (timersSet) {
        const hasAutoplayTimer = Array.from(timersSet).some(timerData => timerData.type === 'autoplay');
        if (hasAutoplayTimer) {
          return;
        }
      }
      
      const delay = instance.config.autoplayDelay || 5000;

      // PHASE 3 HOT FIX #2: Add safety check for destroyed instances
      instance.autoplayInterval = setInterval(() => {
        // Check if instance still exists
        const state = this.state.instances[instance.id];
        if (!state) {
          clearInterval(instance.autoplayInterval);
          instance.autoplayInterval = null;
          return;
        }
        
        // Timed-cycle rules, checked on every beat (_carouselAutoplayAllowed).
        if (!this._carouselAutoplayAllowed(instance)) return;

        // Check if animation is in progress
        if (!state.isAnimating) {
          const productsPerView = this.getProductsPerView(instance);
          const maxIndex = Math.max(0, state.products.length - productsPerView);

          // Check if we are at the last slide
          if (state.currentIndex >= maxIndex) {
            this.slideTo(instance, 0); // Loop back to the start
          } else {
            this.slideNext(instance); // Continue to the next slide
          }
          // Load and decode the card the next beat brings in, a beat ahead.
          this._warmCarouselIncoming(instance);
        }
      }, delay);

      // PHASE 5: Register autoplay timer for tracking
      this.registerTimer(instance.id, instance.autoplayInterval, 'autoplay');

      // PHASE 3 HOT FIX #2: Only setup hover listeners if not already setup
      if (instance.autoplayHoverListenersSetup) {
        return;
      }

      // Pause on hover
      const mouseEnterHandler = () => {
        if (instance.autoplayInterval) {
          const timerId = instance.autoplayInterval;
          
          // PHASE 5 HOT FIX #3: Clear interval AND remove from registry
          clearInterval(timerId);
          this.removeTimerFromRegistry(instance.id, timerId, 'autoplay');
          
          instance.autoplayInterval = null;        
        }
      };

      const mouseLeaveHandler = () => {
        // PHASE 3 HOT FIX #2: Safety check before restarting autoplay
        const state = this.state.instances[instance.id];
        if (!state) {
          return;
        }
        
        if (instance.config.autoplay && !instance.autoplayInterval && !instance.autoplayStoppedByUser) {
          this.setupAutoplay(instance);
        }
      };

      // Mouse only: on touch, a tap fires mouseenter with nothing to end it.
      if (window.matchMedia('(hover: hover)').matches) {
        instance.element.addEventListener('mouseenter', mouseEnterHandler);
        this.registerEventListener(instance.id, instance.element, 'mouseenter', mouseEnterHandler);

        instance.element.addEventListener('mouseleave', mouseLeaveHandler);
        this.registerEventListener(instance.id, instance.element, 'mouseleave', mouseLeaveHandler);
      }

      // PHASE 3 HOT FIX #2: Mark listeners as setup to prevent duplicates
      instance.autoplayHoverListenersSetup = true;
      
      // PHASE 3 HOT FIX #2: Store handlers for potential cleanup
      instance.autoplayHoverHandlers = { mouseEnterHandler, mouseLeaveHandler };
    }

    stopAutoplay(instance) {
      if (instance.autoplayInterval) {
        const timerId = instance.autoplayInterval;
        
        clearInterval(timerId);
        
        // PHASE 5 HOT FIX: Use safe removal helper
        this.removeTimerFromRegistry(instance.id, timerId, 'autoplay');
        
        instance.autoplayInterval = null;
      }
      
      // PHASE 3 HOT FIX #2: Don't clear the hover listeners flag here
      // They should only be cleared on full destruction
    }

    /**
     * CAROUSEL AUTOPLAY: timed-cycle rules. Runs only while at least half the
     * carousel is on screen. Off screen the strip stays where she left it, so
     * it comes back already drawn instead of on cards Chrome never painted
     * (the old timer kept sliding off screen: a stutter on every entry).
     * Each entry waits a full beat before the first slide. A sideways swipe
     * stops it for good; a touch that does not become a swipe resumes it.
     */
    _watchCarouselAutoplay(instance) {
      if (instance.autoplayWatcher) return;
      instance.autoplayInView = false;

      const restart = () => {
        if (!instance.config.autoplay || instance.autoplayStoppedByUser || !instance.autoplayInView) return;
        this.stopAutoplay(instance);
        this.setupAutoplay(instance); // fresh timer: a full beat before the first slide
        this._warmCarouselIncoming(instance);
      };

      const observer = new IntersectionObserver(([entry]) => {
        const inView = entry.intersectionRatio >= 0.5;
        if (inView === instance.autoplayInView) return;
        instance.autoplayInView = inView;
        if (inView) restart();
        else this.stopAutoplay(instance);
      }, { threshold: [0, 0.5] });
      observer.observe(instance.element);
      this.registerObserver(`autoplay-${instance.id}`, observer, 'autoplay-visibility', instance.id);
      instance.autoplayWatcher = observer;

      // Touch start pauses it (handleTouchStart); resume unless she swiped.
      const onTouchEnd = () => { if (!instance.autoplayInterval) restart(); };
      instance.element.addEventListener('touchend', onTouchEnd, { passive: true });
      instance.element.addEventListener('touchcancel', onTouchEnd, { passive: true });
      this.registerEventListener(instance.id, instance.element, 'touchend', onTouchEnd);
      this.registerEventListener(instance.id, instance.element, 'touchcancel', onTouchEnd);
    }

    _carouselAutoplayAllowed(instance) {
      if (!instance.autoplayInView || instance.autoplayStoppedByUser) return false;
      if (!this._photoCycleAllowed('strip')) return false; // hidden tab, reduced motion, Save-Data, perf switch
      try {
        const active = document.activeElement;
        if (active && instance.element.contains(active) && active.matches(':focus-visible')) return false;
      } catch (e) { /* :focus-visible unsupported: no focus pause */ }
      return true;
    }

    /**
     * Load and decode the photos the next autoplay slide shows (the cards in
     * view after it, plus one), so a slide never lands on an unloaded card.
     * Their first photos are lazy and sit outside the strip's clip, so the
     * browser would not fetch them until they were already sliding in.
     */
    _warmCarouselIncoming(instance) {
      const state = this.state.instances[instance.id];
      if (!state || !state.products || !instance.track) return;
      // Where the next beat lands: same rules as the autoplay tick and slideNext.
      const perView = this.getProductsPerView(instance);
      const whole = Math.max(1, Math.floor(perView));
      const isMulticol = instance.element.classList.contains('lumistelle-grid--mobile-multicol') && window.innerWidth < 990;
      const loopBack = state.currentIndex >= Math.max(0, state.products.length - perView);
      const next = loopBack ? 0 : Math.min(state.currentIndex + (isMulticol ? 1 : whole), Math.max(0, state.products.length - whole));
      const cards = instance.track.querySelectorAll('.lumistelle-carousel__product');
      const to = Math.min(cards.length, next + Math.ceil(perView) + 1);
      for (let i = next; i < to; i++) {
        const card = cards[i];
        const track = card.querySelector('.lumistelle-image-track');
        const index = card.currentImageIndex || 0;
        const slide = track ? track.children[index] : null;
        const img = slide ? slide.querySelector('img') : card.querySelector('.lumistelle-carousel__product-image');
        if (!img) continue;
        if (img.loading === 'lazy') img.loading = 'eager';
        if (track) this._warmCardPhoto(track, index);
      }
    }

    renderProducts(instance) {
      const instanceId = instance.id;
      const state = this.state.instances[instanceId];
      if (!state || !state.products || !instance.track) return;

      instance.track.innerHTML = '';
      const fragment = document.createDocumentFragment();
      
      state.products.forEach((product, index) => {
        let productEl = this.productPool.pop() || this.templateCache.get('product').cloneNode(true);
        // PHASE 6: Only store product ID, not full data reference
        productEl.dataset.productId = product.id;
        fragment.appendChild(productEl);
      });
      
      instance.track.appendChild(fragment);
    }

    hydrateCards(instance) {
      const products = instance.track.querySelectorAll('.lumistelle-carousel__product');
      products.forEach((productEl, index) => {
        if (productEl.hasAttribute('data-hydrated')) return;
        
        const productId = parseInt(productEl.dataset.productId, 10);
        const productData = this.variantData.get(productId);

        if (productData) {
          // PHASE 6: Don't attach data to element (causes memory leak)
          // Call the master function to populate and make interactive
          this.updateProductElement(productEl, productData, index, instance.config);
          productEl.setAttribute('data-hydrated', 'true');
        }
      });
      document.dispatchEvent(new CustomEvent('lumistelle:cards:rendered'));
      this.debouncedProcessHydration();
    }

    createTemplates() {
      const productTemplate = document.createElement('div');
      productTemplate.className = 'lumistelle-carousel__product';
      // The shell is now completely empty.
      this.templateCache.set('product', productTemplate);
    }

    initializeProductPool() {
      for (let i = 0; i < 20; i++) {
        this.productPool.push(this.templateCache.get('product').cloneNode(true));
      }
    }

    getProductFromPool(productData, index) {
      let productEl = this.productPool.pop() || this.templateCache.get('product').cloneNode(true);
      this.updateProductElement(productEl, productData, index);
      return productEl;
    }

    returnToPool(element) {
      if (this.productPool.length < this.poolSize) {
        this.productPool.push(element);
      }
    }

    updateProductElement(element, productData, index, config) {
      // === LITE CARD: SSR cards are complete — never hydrate ===
      if (element.hasAttribute('data-card-lite')) return;

      element.dataset.productId = productData.id;

      // Generate and cache the media-to-color map for this product
      if (!productData.mediaToColorMap) {
        productData.mediaToColorMap = this._createMediaToColorMap(productData);
      }

      element.dataset.index = index;

      // Apply card layout modifier class
      element.classList.remove('lumistelle-carousel__product--a1', 'lumistelle-carousel__product--a2');
      if (config && config.cardLayout && config.cardLayout !== 'classic') {
        element.classList.add(`lumistelle-carousel__product--${config.cardLayout}`);
      }

      // === PHASE 4: Check if we need to build the card HTML first ===
      // If mediaContainer doesn't exist, it's a client-rendered card that needs HTML
      let needsHtmlBuild = !element.querySelector('.lumistelle-carousel__product-media');
      
      if (needsHtmlBuild) {
        // Invalidate any existing cache since we're rebuilding HTML
        this._invalidateCardCache(element);
        
        const isGrid = element.closest('.lumistelle-grid-section');
        let mediaHTML = '';
        const ppv = config?.productsPerView || {};
        const sizesAttr = `(min-width: 2301px) calc(100vw / ${ppv.xl_desktop || 6}), (min-width: 1801px) calc(100vw / ${ppv.large_desktop || 5}), (min-width: 990px) calc(100vw / ${ppv.desktop || 4}), (min-width: 750px) 33vw, calc(100vw / ${ppv.mobile || 1})`;

        if (isGrid && productData.images && productData.images.length > 1) {
          mediaHTML += '<div class="lumistelle-image-track">';
          productData.images.forEach((image, imgIndex) => {
            if (imgIndex === 0) {
              mediaHTML += `<div class="lumistelle-image-slide"><img class="lumistelle-carousel__product-image" src="${image.src}" srcset="${image.srcset}" sizes="${sizesAttr}" alt="${productData.title}" loading="lazy"></div>`;
            } else {
              mediaHTML += `<div class="lumistelle-image-slide"><img class="lumistelle-carousel__product-image" src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7" data-src="${image.src}" data-srcset="${image.srcset}" sizes="${sizesAttr}" alt="${productData.title}" loading="lazy"></div>`;
            }
          });
          mediaHTML += '</div>';
        } else {
          mediaHTML = `<a href="${productData.url}" class="lumistelle-carousel__product-link"><img class="lumistelle-carousel__product-image" src="${productData.image}" srcset="${productData.srcset}" sizes="${sizesAttr}" alt="${productData.title}" loading="lazy"></a>`;
        }
        
        const fullCardHTML = `
          <div class="lumistelle-carousel__product-media">
            ${mediaHTML}
            ${productData.images && productData.images.length > 1 ? `
              <div class="lumistelle-carousel__image-nav-wrapper">
                <button class="lumistelle-carousel__image-nav lumistelle-carousel__image-nav--prev" aria-label="Previous image">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </button>
                <button class="lumistelle-carousel__image-nav lumistelle-carousel__image-nav--next" aria-label="Next image">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M9 6L15 12L9 18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </button>
              </div>
            ` : ''}
            ${productData.images && productData.images.length > 1 ? `<button class="lum-gallery-trigger" aria-label="Open fullscreen gallery" data-product-id="${productData.id}"><svg width="25" height="25" viewBox="0 0 24 24" fill="none" overflow="visible"><g style="fill:none" stroke="black" stroke-opacity="0.3" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" transform="translate(0,0.5)"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></g><g stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></g></svg></button>` : ''}
            ${config && config.showViewProduct !== false ? `<a href="${productData.url}" class="lumistelle-carousel__view-product" aria-hidden="true" tabindex="-1">${config.viewProductText || 'View Product'}</a>` : ''}
            <div class="lumistelle-carousel__image-progress"><div class="lumistelle-carousel__image-progress-fill">&zwnj;</div></div>
            <div class="lumistelle-carousel__badges"></div>
            <div class="lumistelle-carousel__material-badge"></div>
            ${config.showQuickView ? (() => { const co = productData.options_with_values?.find(o => o.name.toLowerCase() === 'color'); const cc = co ? co.values.length : 0; return cc > 1 ? `<button class="lumistelle-carousel__color-indicator" aria-label="${cc} colors available" data-quick-view="${productData.handle}"><span class="lumistelle-carousel__color-wheel"></span><span class="lumistelle-carousel__color-count">+${cc}</span></button>` : ''; })() : ''}
            <div class="lumistelle-carousel__actions">
              ${config.showQuickView ? `<button class="lumistelle-carousel__quick-view" aria-label="Quick view" data-quick-view="${productData.handle}"><svg width="25" height="25" viewBox="0 0 24 24" fill="none" overflow="visible"><g style="fill:none" stroke="black" stroke-opacity="0.3" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" transform="translate(0,0.5)"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></g><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/></svg></button>` : ''}
              <button class="lumistelle-carousel__wishlist iwish-btn iwishcheck iWishAddColl" data-product="${productData.id}" data-variant="${productData.variants[0].id}" aria-label="Add to wishlist"><svg width="25" height="25" viewBox="0 0 24 24" fill="none"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" stroke="currentColor" stroke-width="2"/></svg></button>
              </div>
          </div>
          <div class="lumistelle-carousel__product-info">
            <div class="lumistelle-carousel__product-vendor"></div>
            <h3 class="lumistelle-carousel__product-title"><a href="#"></a></h3>
            <div class="lumistelle-carousel__product-rating"><div class="lumistelle-carousel__stars"></div><span class="lumistelle-carousel__reviews-count"></span></div>
            <div class="lumistelle-carousel__product-price"><span class="lumistelle-carousel__price--current"></span><span class="lumistelle-carousel__price--compare"></span><span class="lumistelle-carousel__discount"></span><span class="lumistelle-stock-status"></span></div>
            <div class="lumistelle-carousel__variants">
              <div class="lumistelle-carousel__variants-group lumistelle-carousel__variants-group--color" style="display: none;">
                <div class="lumistelle-carousel__variants-label"></div>
                <div class="lumistelle-carousel__colors"></div>
              </div>
              <div class="lumistelle-carousel__variants-group lumistelle-carousel__variants-group--size" style="display: none;">
                <div class="lumistelle-carousel__variants-label"></div>
                <div class="lumistelle-carousel__sizes"></div>
              </div>
            </div>
                <div class="lumistelle-size-guide-container">
                <a href="#" class="lumistelle-size-guide-trigger" data-action="open-size-chart" data-product-handle="${productData.handle}">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L3 8.4a2.4 2.4 0 0 1 0-3.4l2.6-2.6a2.4 2.4 0 0 1 3.4 0L12 5.3"></path><path d="m18 2-2.2 2.2"></path><path d="M7.5 10.5 9 9"></path><path d="M10.5 7.5 12 6"></path><path d="m15 13.5-1.5-1.5"></path><path d="m12 16.5-1.5-1.5"></path></svg>
                <span>Size Guide</span>
                </a>
                <div class="lumistelle-tooltip" tabindex="0" role="button" aria-label="More information">
                <svg class="lumistelle-tooltip__icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                <span class="lumistelle-tooltip__text">Real Measurements</span>
                </div>
                </div>
            <button class="lumistelle-carousel__quick-add"><span class="lumistelle-carousel__quick-add-text">Add to Cart</span><span class="lumistelle-carousel__quick-add-loading"></span></button>
            ${config.showQuickView ? `<button class="lumistelle-carousel__quick-view-compact" aria-label="Quick view" data-quick-view="${productData.handle}"><span class="lumistelle-carousel__compact-btn-plus">+</span><span class="lumistelle-carousel__compact-btn-text">Options</span></button>` : ''}
          </div>
        `;
        element.innerHTML = fullCardHTML;
      }

      // === PHASE 4: Get cached element references ===
      const cached = this._getCardElements(element);

      // Update basic product info using cached references
      if (cached.titleLink) {
        cached.titleLink.href = productData.url;
        cached.titleLink.textContent = productData.title;
      }
      if (cached.viewProductLink) {
        cached.viewProductLink.href = productData.url;
      }
      if (cached.vendor) {
        cached.vendor.textContent = productData.vendor;
      }

      // Apply visibility settings using cached references
      if (config) {
        if (cached.vendor) {
          cached.vendor.style.display = config.showVendor ? '' : 'none';
        }
        if (cached.ratingContainer) {
          cached.ratingContainer.style.display = config.showRating ? '' : 'none';
        }
        if (cached.stockStatus) {
          cached.stockStatus.style.display = config.showStockLevel ? '' : 'none';
        }
        if (cached.quickAddButton) {
          cached.quickAddButton.style.display = config.showQuickAdd ? '' : 'none';
        }
        if (cached.quickViewButton) {
          cached.quickViewButton.style.display = config.showQuickView ? '' : 'none';
        }
      }

      // Call helper methods with cached elements
      this.updateProductPrice(element, productData, config, cached);
      this.updateProductBadges(element, productData, config, cached);
      this.updateMaterialBadge(element, productData, config, cached);
      this.updateStockIndicator(element, productData, cached);
      this.updateRating(element, productData, cached);
      this.setupColorSwatches(element, productData, config, cached);
      this.setupSizeSelectors(element, productData, config, cached);
      
      let initialImageIndex = 0;
      if (productData.images && productData.images.length > 0 && productData.featured_image_id) {
        const foundIndex = productData.images.findIndex(imageObj => imageObj.id === productData.featured_image_id);
        if (foundIndex !== -1) initialImageIndex = foundIndex;
      }
      this.setupImageNavigation(element, productData, initialImageIndex, cached);

      if (window.LumistelleCarouselFeatures) {
        window.LumistelleCarouselFeatures.handleVariantChange(element, config);
      }
    }

    updateProductPrice(element, productData, config = {}, cachedElements = null) {
      // === PHASE 4: Use cached elements or fall back to querySelector ===
      const cached = cachedElements || this._getCardElements(element);
      
      const current = cached.currentPrice;
      const compare = cached.comparePrice;
      const discount = cached.discount;
      
      const hideDecimals = config.hidePriceDecimals || false;
      if (current) current.textContent = this.formatMoney(productData.price, hideDecimals);

      if (compare && productData.compare_at_price > productData.price) {
        compare.textContent = this.formatMoney(productData.compare_at_price, hideDecimals);
        compare.style.display = '';
        
        // KEY FIX: Only show the discount if the setting is enabled
        if (discount && config.showDiscountPercentage) {
          discount.textContent = `-${productData.discount_percentage}%`;
          discount.style.display = '';
        } else if (discount) {
          discount.style.display = 'none';
        }
      } else {
        if (compare) compare.style.display = 'none';
        if (discount) discount.style.display = 'none';
      }
    }

    updateProductBadges(element, productData, config = {}, cachedElements = null) {
      // === PHASE 4: Use cached elements or fall back to querySelector ===
      const cached = cachedElements || this._getCardElements(element);
      
      const badgeContainer = cached.badgeContainer;
      if (!badgeContainer) return;
      
      badgeContainer.innerHTML = '';
      if (config.showSaleBadge !== false && productData.compare_at_price > productData.price) badgeContainer.innerHTML += `<span class="lumistelle-carousel__badge lumistelle-carousel__badge--sale">Sale</span>`;
      if (config.showNewBadge !== false && productData.is_new) badgeContainer.innerHTML += `<span class="lumistelle-carousel__badge lumistelle-carousel__badge--new">New</span>`;
      if (config.showBestsellerBadge !== false && productData.is_bestseller) badgeContainer.innerHTML += `<span class="lumistelle-carousel__badge lumistelle-carousel__badge--bestseller">Bestseller</span>`;
    }

    // ======================================================================
    // MATERIAL BADGE SYSTEM
    // ======================================================================

    /**
     * Material name normalization map.
     * Maps aliases to canonical keys that match schema image pickers.
     */
    static MATERIAL_ALIASES = {
      'cotton': 'cotton',
      'linen': 'linen',
      'rayon': 'rayon',
      'viscose': 'rayon',
      'modal': 'modal',
      'lyocell': 'modal',
      'tencel': 'modal',
      'triacetate': 'modal',
      'triacetic': 'modal',
      'silk': 'silk',
      'polyester': 'polyester',
      'nylon': 'polyester',
      'spandex': 'spandex',
      'elastane': 'spandex',
      'acrylic': 'acrylic',
      'wool': 'wool',
      'bamboo': 'rayon',
      'hemp': 'linen'
    };

    /**
     * Materials that qualify as "natural" for badge display.
     */
    static NATURAL_MATERIALS = new Set(['cotton', 'linen', 'rayon', 'modal', 'silk', 'wool']);

    /**
     * Abbreviation map for material composition display on product cards.
     * Keyed by raw parsed material name (lowercase). Unlisted materials display in full.
     */
    static COMPOSITION_ABBREVIATIONS = {
      'polyester': 'Poly.',
      'spandex': 'Span.',
      'elastane': 'Elast.'
    };

    /**
     * Parse material composition from a product description string.
     * Handles both "65% rayon" and "cotton 100%" formats.
     * 
     * @param {string} description - Raw product description HTML
     * @returns {Object|null} Parsed material data or null if not found
     */
    _parseMaterialComposition(description) {
      if (!description) return null;

      // Strip HTML tags
      const text = description.replace(/<[^>]*>/g, ' ').toLowerCase();

      // Find "material composition" or standalone "material:" segment
      let matIdx = text.indexOf('material composition');
      let labelLen = 'material composition'.length;
      if (matIdx === -1) {
        // Fallback: "Material:" without "composition" (some Trendsi products)
        const fallback = text.match(/\bmaterial\s*:/);
        if (fallback) {
          matIdx = fallback.index;
          labelLen = fallback[0].length;
        }
      }
      if (matIdx === -1) return null;

      // Extract the substring after the label
      let segment = text.substring(matIdx + labelLen);
      // Remove leading colon and whitespace
      segment = segment.replace(/^[\s:]+/, '');

      // Truncate at common terminators
      const terminators = ['care instructions', 'imported', 'product measurements', 'features', 'stretch', 'sheer', 'body:', 'lining', 'lower', '* '];
      for (const term of terminators) {
        const termIdx = segment.indexOf(term);
        if (termIdx !== -1) {
          segment = segment.substring(0, termIdx);
        }
      }

      // Clean up bullet characters
      segment = segment.replace(/[*•]/g, '').trim();

      if (!segment) return null;

      // Extract all {number}%{material} pairs via regex
      // Handles comma-separated, space-separated, and no-delimiter formats in one pass
      // Section labels (SELF, UPPER:) contain no digit% pattern and are naturally skipped
      const materials = [];
      const pairRegex = /(\d+(?:\.\d+)?)\s*%\s*([a-z]+)/g;
      let match;
      while ((match = pairRegex.exec(segment)) !== null) {
        const percentage = Math.round(parseFloat(match[1]));
        const name = match[2];
        if (percentage > 0 && percentage <= 100) {
          const key = LumistelleCarouselEngine.MATERIAL_ALIASES[name] || name;
          const isNatural = LumistelleCarouselEngine.NATURAL_MATERIALS.has(key);
          const displayName = name.charAt(0).toUpperCase() + name.slice(1);
          materials.push({ key, percentage, isNatural, displayName });
        }
      }

      if (materials.length === 0) return null;

      // Sort by percentage descending
      materials.sort((a, b) => b.percentage - a.percentage);

      const dominant = materials[0];
      const naturalMaterials = materials.filter(m => m.isNatural);
      const naturalPercentage = naturalMaterials.reduce((sum, m) => sum + m.percentage, 0);

      let blendType;
      if (materials.length === 1 && dominant.percentage === 100 && dominant.isNatural) {
        blendType = 'pure';
      } else if (naturalMaterials.length >= 2) {
        blendType = 'multi-natural';
      } else if (naturalMaterials.length === 1 && dominant.isNatural) {
        blendType = 'single-natural';
      } else {
        blendType = 'synthetic-majority';
      }

      return {
        materials,
        dominant,
        naturalPercentage,
        isPure: blendType === 'pure',
        blendType,
        source: 'description'
      };
    }

    /**
     * Get material data for a product.
     * Uses pre-parsed data from JSON island if available, otherwise parses from description.
     * Checks for tag overrides first.
     * 
     * @param {Object} productData - The product data object
     * @returns {Object|null} Material data
     */
    _getMaterialData(productData) {
      // If material_data already exists (from Liquid parser via JSON island), use it
      if (productData.material_data && productData.material_data.materials) {
        // Validate Liquid parser output before trusting it.
        // Garbled keys like "linen rayon", "upper: cotton", "cotton spandex" indicate
        // the Liquid parser couldn't handle this format (space-separated, section-prefixed).
        // Discard and fall through to the JS regex parser which handles all formats.
        const md = productData.material_data;
        const ALIASES = LumistelleCarouselEngine.MATERIAL_ALIASES;
        const hasRecognizedMaterial = md.materials.some(m => ALIASES[m.key] !== undefined);
        if (hasRecognizedMaterial) {
          if (!md.dominant) {
            const sorted = [...md.materials].sort((a, b) => b.percentage - a.percentage);
            md.dominant = sorted[0];
            md.materials = sorted;
            // Add isNatural flags if missing (from tag source)
            md.materials.forEach(m => {
              if (m.isNatural === undefined) {
                m.isNatural = LumistelleCarouselEngine.NATURAL_MATERIALS.has(m.key);
              }
              if (!m.displayName) {
                m.displayName = m.key.charAt(0).toUpperCase() + m.key.slice(1);
              }
            });
            const naturalMats = md.materials.filter(m => m.isNatural);
            md.naturalPercentage = naturalMats.reduce((sum, m) => sum + m.percentage, 0);
            md.isPure = md.materials.length === 1 && md.dominant.percentage === 100 && md.dominant.isNatural;
            if (md.isPure) md.blendType = 'pure';
            else if (naturalMats.length >= 2) md.blendType = 'multi-natural';
            else if (naturalMats.length === 1 && md.dominant.isNatural) md.blendType = 'single-natural';
            else md.blendType = 'synthetic-majority';
          }
          return md;
        }
      }

      // Check for tag overrides
      if (productData.tags) {
        const tags = Array.isArray(productData.tags) ? productData.tags : 
                     (typeof productData.tags === 'string' ? productData.tags.split(',').map(t => t.trim()) : []);
        const materialTags = tags.filter(t => t.toLowerCase().startsWith('material-'));
        if (materialTags.length > 0) {
          const materials = materialTags.map(tag => {
            const parts = tag.toLowerCase().replace('material-', '').split(':');
            const originalName = parts[0];
            const key = LumistelleCarouselEngine.MATERIAL_ALIASES[originalName] || originalName;
            const percentage = parseInt(parts[1]) || 100;
            const isNatural = LumistelleCarouselEngine.NATURAL_MATERIALS.has(key);
            const displayName = originalName.charAt(0).toUpperCase() + originalName.slice(1);
            return { key, percentage, isNatural, displayName };
          }).sort((a, b) => b.percentage - a.percentage);

          const dominant = materials[0];
          const naturalMats = materials.filter(m => m.isNatural);
          const naturalPercentage = naturalMats.reduce((sum, m) => sum + m.percentage, 0);
          let blendType = 'synthetic-majority';
          if (materials.length === 1 && dominant.percentage === 100 && dominant.isNatural) blendType = 'pure';
          else if (naturalMats.length >= 2) blendType = 'multi-natural';
          else if (naturalMats.length === 1 && dominant.isNatural) blendType = 'single-natural';

          return { materials, dominant, naturalPercentage, isPure: blendType === 'pure', blendType, source: 'tags' };
        }
      }

      // Fall back to parsing from description
      return this._parseMaterialComposition(productData.description);
    }

    /**
     * Render or update the material badge on a product card.
     * 
     * @param {HTMLElement} element - The product card element
     * @param {Object} productData - The product data object
     * @param {Object} config - Section config (must contain materialBadge block)
     * @param {Object} cachedElements - Cached DOM references
     */
    updateMaterialBadge(element, productData, config = {}, cachedElements = null) {
      const mbConfig = config.materialBadge;
      if (!mbConfig || !mbConfig.enabled) return;

      const cached = cachedElements || this._getCardElements(element);
      const container = cached.materialBadgeContainer;
      if (!container) return;

      // Clear any existing badge
      container.innerHTML = '';

      // Get material data (pre-parsed or parse now)
      const matData = this._getMaterialData(productData);
      if (!matData || !matData.materials || matData.materials.length === 0) return;

      // --- Badge image (only for natural-dominant products with image available) ---
      let badgeImageUrl = '';
      if (matData.dominant && matData.dominant.isNatural) {
        const dominantKey = matData.dominant.key;
        // Check if this material type is toggled on
        if (!mbConfig.materialToggles || mbConfig.materialToggles[dominantKey] !== false) {
          const mode = mbConfig.mode || 'dominant';

          if (matData.blendType === 'pure' || matData.blendType === 'single-natural') {
            badgeImageUrl = mbConfig.images?.[dominantKey] || '';
          } else if (matData.blendType === 'multi-natural') {
            if (mode === 'dominant') {
              badgeImageUrl = mbConfig.images?.[dominantKey] || '';
            } else if (mode === 'blend') {
              badgeImageUrl = mbConfig.images?.blend || mbConfig.images?.[dominantKey] || '';
            } else if (mode === 'composite') {
              const naturalMats = matData.materials.filter(m => m.isNatural).sort((a, b) => a.key.localeCompare(b.key));
              if (naturalMats.length >= 2) {
                const compositeKey = `${naturalMats[0].key}_${naturalMats[1].key}`;
                badgeImageUrl = mbConfig.images?.[compositeKey] || '';
              }
              if (!badgeImageUrl) badgeImageUrl = mbConfig.images?.blend || '';
              if (!badgeImageUrl) badgeImageUrl = mbConfig.images?.[dominantKey] || '';
            }
          }
        }
      }

      // --- Composition text (all products with material data) ---
      const ABBR = LumistelleCarouselEngine.COMPOSITION_ABBREVIATIONS;
      const compositionText = matData.materials.map(m => {
        const rawName = (m.displayName || m.key).toLowerCase();
        const display = ABBR[rawName] || m.displayName || (m.key.charAt(0).toUpperCase() + m.key.slice(1));
        return `${m.percentage}% ${display}`;
      }).join(' \u00B7 ');

      // --- Build HTML ---
      let html = '';

      if (badgeImageUrl) {
        const size = mbConfig.size || 40;
        html += `<img class="lumistelle-carousel__material-badge-img" src="${badgeImageUrl}" alt="${matData.dominant.key}" width="${size}" height="${size}" loading="lazy">`;
      }

      if (compositionText) {
        html += `<span class="lumistelle-carousel__material-badge-text">${compositionText}</span>`;
      }

      if (html) {
        container.innerHTML = html;
      }
    }

    /**
     * Generate the text label for a material badge.
     * 
     * @param {Object} matData - Parsed material data
     * @param {string} format - Text format: 'percentage', 'blend_label', 'name_only'
     * @param {string} context - 'dominant' or 'blend'
     * @returns {string} The formatted text
     */
    _getMaterialBadgeText(matData, format, context) {
      const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
      const getName = (m) => m.displayName || capitalize(m.key);
      const dominant = matData.dominant;
      const naturalMats = matData.materials.filter(m => m.isNatural);

      if (context === 'blend' && naturalMats.length >= 2) {
        // Multi-natural blend
        if (format === 'percentage') {
          return naturalMats.map(m => `${m.percentage}% ${getName(m)}`).join(', ');
        } else if (format === 'blend_label') {
          return naturalMats.map(m => getName(m)).join(' & ');
        } else {
          return 'Natural Blend';
        }
      }

      // Single material context
      if (format === 'percentage') {
        if (matData.isPure) {
          return `100% ${getName(dominant)}`;
        }
        return `${dominant.percentage}% ${getName(dominant)}`;
      } else if (format === 'blend_label') {
        if (matData.isPure) {
          return getName(dominant);
        }
        return `${getName(dominant)} Blend`;
      } else {
        return getName(dominant);
      }
    }

    updateStockIndicator(element, productData, cachedElements = null) {
      // === PHASE 4: Use cached elements or fall back to querySelector ===
      const cached = cachedElements || this._getCardElements(element);
      
      const stockStatus = cached.stockStatus;
      if (!stockStatus) return;
      
      stockStatus.className = 'lumistelle-stock-status';
      if (!productData.available) {
        stockStatus.classList.add('lumistelle-stock-out');
        stockStatus.textContent = 'Out of stock';
      } else if (productData.inventory_quantity > 0 && productData.inventory_quantity <= 5) {
        stockStatus.classList.add('lumistelle-stock-low');
        stockStatus.textContent = `Only ${productData.inventory_quantity} left`;
      } else {
        stockStatus.classList.add('lumistelle-stock-available');
        stockStatus.textContent = 'In stock';
      }
    }

    updateRating(element, productData, cachedElements = null) {
      // === PHASE 4: Use cached elements or fall back to querySelector ===
      const cached = cachedElements || this._getCardElements(element);
      
      const ratingContainer = cached.ratingContainer;
      if (!ratingContainer) return;
      
      if (!productData.rating || productData.rating <= 0) {
        ratingContainer.style.display = 'none';
        return;
      }
      
      ratingContainer.style.display = '';
      
      // Make rating clickable to open reviews modal
      if (productData.reviews_count > 0) {
        ratingContainer.setAttribute('role', 'button');
        ratingContainer.setAttribute('tabindex', '0');
        ratingContainer.setAttribute('aria-label', `${productData.rating} out of 5 stars, ${productData.reviews_count} reviews. Click to read reviews.`);
      }

      const starsContainer = cached.starsContainer;
      const countElement = cached.reviewsCount;
      const rating = parseFloat(productData.rating) || 0;
      
      if (starsContainer) {
        // Read schema-driven CSS custom properties as literal color values.
        // This avoids browser inconsistencies with var() inside SVG attributes.
        const styles = getComputedStyle(ratingContainer);
        const filledColor = styles.getPropertyValue('--lumistelle-carousel-star-filled').trim() || '#ffc107';
        const emptyColor = styles.getPropertyValue('--lumistelle-carousel-star-empty').trim() || '#e5e5e5';
        const starPath = 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z';
        
        let starsHTML = '';
        for (let i = 1; i <= 5; i++) {
          const diff = rating - (i - 1);
          if (diff >= 1) {
            // Full star
            starsHTML += `<svg width="16" height="16" viewBox="0 0 24 24"><path d="${starPath}" fill="${filledColor}" stroke="none"/></svg>`;
          } else if (diff > 0) {
            // Partial star — linearGradient with hard stop
            const pct = Math.round(diff * 100);
            starsHTML += `<svg width="16" height="16" viewBox="0 0 24 24"><path d="${starPath}" fill="${emptyColor}" stroke="none"/><path d="${starPath}" fill="${filledColor}" stroke="none" style="clip-path:inset(0 ${100 - pct}% 0 0)"/></svg>`;
          } else {
            // Empty star
            starsHTML += `<svg width="16" height="16" viewBox="0 0 24 24"><path d="${starPath}" fill="${emptyColor}" stroke="none"/></svg>`;
          }
        }
        starsContainer.innerHTML = starsHTML;
      }
      
      if (countElement) {
        countElement.textContent = productData.reviews_count > 0 ? `(${productData.reviews_count})` : '';
      }
    }

    setupColorSwatches(element, productData, config, cachedElements = null) {
      // === PHASE 4: Use cached elements or fall back to querySelector ===
      const cached = cachedElements || this._getCardElements(element);
      
      const colorGroup = cached.colorGroup;
      if (!colorGroup || !config.showColorSwatches) {
        if (colorGroup) colorGroup.style.display = 'none';
        return;
      }

      const colorsContainer = cached.colorsContainer;
      if (!colorsContainer) return;
      
      const colors = Array.from(new Map(productData.variants.filter(v => v.option1).map(v => [v.option1, { value: v.option1 }])).values());
      
      if (colors.length <= 1) {
        colorGroup.style.display = 'none';
        return;
      }

      // Show the group and set the label
      colorGroup.style.display = '';
      const colorLabel = cached.colorLabel;
      const colorOption = productData.options_with_values?.find(opt => opt.position === 1);
      if (colorLabel) {
        colorLabel.textContent = colorOption ? colorOption.name : 'Color';
      }

      colorsContainer.innerHTML = '';
      const fragment = document.createDocumentFragment();
      const firstAvailableVariant = productData.variants.find(v => v.available);

      colors.forEach((color) => {
        const swatch = document.createElement('button');
        swatch.className = 'lumistelle-carousel__color';
        if (firstAvailableVariant && color.value === firstAvailableVariant.option1) {
          swatch.classList.add('active');
        }
        swatch.dataset.color = color.value;
        swatch.setAttribute('aria-label', color.value);

        let imageUrl = null;
        
        if (productData.colorToSwatchUrlMap) {
          imageUrl = productData.colorToSwatchUrlMap[color.value];
        } else {
          const variantForSwatchImage = productData.variants.find(v => v.option1 === color.value && v.featured_image_id);
          if (variantForSwatchImage) {
            const imageObject = productData.images.find(img => img.id === variantForSwatchImage.featured_image_id);
            if (imageObject) {
              imageUrl = imageObject.swatch_url;
            }
          }
        }

        if (imageUrl) {
          const img = document.createElement('img');
          img.src = imageUrl;
          img.alt = color.value;
          img.loading = 'lazy';
          img.width = 50;
          img.height = 62;
          swatch.appendChild(img);
        } else {
          swatch.dataset.needsHydration = 'true';
          swatch.dataset.productHandle = productData.handle;
          const colorMap = {'black':'#000','white':'#fff','red':'#f00','blue':'#00f','green':'#008000','yellow':'#ff0','pink':'#ffc0cb','purple':'#800080','orange':'#ffa500','brown':'#a52a2a','grey':'#808080','gray':'#808080','navy':'#000080','teal':'#008080','burgundy':'#800020','olive':'#800080','khaki':'#c3b091','cream':'#fffdd0','beige':'#f5f5dc','tan':'#d2b48c','coral':'#ff7f50','lavender':'#e6e6fa','mint':'#98ff98','rose':'#ff007f','gold':'#ffd700','silver':'#c0c0c0'};
          swatch.style.backgroundColor = colorMap[color.value.toLowerCase()] || '#ccc';
          this._scheduleForHydration(swatch, productData.handle, color.value);
        }

        fragment.appendChild(swatch);
      });

      if (!fragment.querySelector('.active') && fragment.firstChild) {
        fragment.firstChild.classList.add('active');
      }
      colorsContainer.appendChild(fragment);
    }

    setupSizeSelectors(element, productData, config, cachedElements = null) {
      // === PHASE 4: Use cached elements or fall back to querySelector ===
      const cached = cachedElements || this._getCardElements(element);
      
      const sizeGroup = cached.sizeGroup;
      if (!sizeGroup || !config.showSizeSwatches) {
        if (sizeGroup) sizeGroup.style.display = 'none';
        return;
      }

      const sizesContainer = cached.sizesContainer;
      if (!sizesContainer) return;
      
      const sizes = Array.from(new Map(productData.variants.filter(v => v.option2).map(v => [v.option2, { value: v.option2 }])).values());
      
      if (sizes.length === 0) {
        sizeGroup.style.display = 'none';
        return;
      }

      // Show the group and set the label
      sizeGroup.style.display = '';
      const sizeLabel = cached.sizeLabel;
      const sizeOption = productData.options_with_values?.find(opt => opt.position === 2);
      if (sizeLabel) {
        sizeLabel.textContent = sizeOption ? sizeOption.name : 'Size';
      }

      sizesContainer.innerHTML = '';
      const fragment = document.createDocumentFragment();
      const firstAvailableVariant = productData.variants.find(v => v.available);

      sizes.forEach((size) => {
        const button = document.createElement('button');
        button.className = 'lumistelle-carousel__size';
        if (firstAvailableVariant && size.value === firstAvailableVariant.option2) {
          button.classList.add('active');
        }
        button.dataset.size = size.value;
        button.textContent = size.value;
        fragment.appendChild(button);
      });

      if (!fragment.querySelector('.active') && fragment.firstChild) {
        fragment.firstChild.classList.add('active');
      }
      sizesContainer.appendChild(fragment);
    }

    // ======================================================================
    // IMAGE GALLERY NAVIGATION (NEW)
    // ======================================================================
    setupImageNavigation(element, productData, initialIndex = 0, cachedElements = null) {
      // === PHASE 4: Use cached elements or fall back to querySelector ===
      const cached = cachedElements || this._getCardElements(element);
      
      const mediaContainer = cached.mediaContainer;
      const progressBar = cached.imageProgressBar;
      const progressFill = cached.imageProgressFill;
      const images = productData?.images;
      
      if (!mediaContainer) return;

      if (!images || images.length <= 1) {
        // Hide progress bar for single-image products
        if (progressBar) progressBar.style.display = 'none';
        return;
      }

      element.currentImageIndex = initialIndex;
      element.totalImages = images.length;
      element.imageTouchStartX = 0;
      element.imageTouchStartY = 0;
      element.imageSwipeDirection = null;
      element.isImageLoading = false;
      element.preloadedImageUrls = new Set();

      // Set initial progress bar position
      if (progressFill) {
        const progress = ((initialIndex + 1) / images.length) * 100;
        progressFill.style.width = `${progress}%`;
      }

      // Add event listeners
      mediaContainer.addEventListener('click', this.handleImageClick.bind(this));
      this.updateImageNavArrows(element, cached); // Set initial button state
    }

    handleImageClick(e) {
      const productEl = e.target.closest('.lumistelle-carousel__product');
      if (!productEl) return;

      const prevArrow = e.target.closest('.lumistelle-carousel__image-nav--prev');
      const nextArrow = e.target.closest('.lumistelle-carousel__image-nav--next');

      if (prevArrow || nextArrow) {
        e.preventDefault();
        e.stopPropagation(); // Stop event from bubbling to other handlers
        this.changeImage(productEl, prevArrow ? -1 : 1);
        return; // Exit the function since we've handled the click
      }

      // --- NEW: DRAG vs. CLICK PREVENTION for IMAGE CYCLING ---
      const instance = this.getInstance(productEl);
      if (instance && instance.hasDragged) {
        // This "click" was the end of a carousel drag, not an intent to cycle images.
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // --- END NEW ---

      const instanceEl = productEl.closest('.lumistelle-grid-section, .lumistelle-carousel');
      const isGrid = instanceEl && instanceEl.classList.contains('lumistelle-grid-section');

      // --- DEFINITIVE FIX ---
      // If the click is on an action button or View Product link, let it through.
      const actionButton = e.target.closest('.lumistelle-carousel__quick-view, .lumistelle-carousel__quick-view-compact, .lumistelle-carousel__color-indicator, .lumistelle-carousel__wishlist, .lum-gallery-trigger');
      if (actionButton) {
        return;
      }

      // Let View Product link navigate natively
      const viewProductLink = e.target.closest('.lumistelle-carousel__view-product');
      if (viewProductLink) {
        return;
      }

      // If a swipe just happened on the carousel, block the ghost click.
      if (this.state.ui.isSwiping) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      
      // If we reach here, it's a valid tap on a carousel image.
      const clickImages = this._getCardImages(productEl);
      if (!clickImages || clickImages.length <= 1) return;

      e.preventDefault();
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickZone = rect.width / 2;
      this.changeImage(productEl, clickX < clickZone ? -1 : 1);
    }

    /**
     * LITE CARD: image list resolver. Prefers variantData (full cards);
     * falls back to the DOM slides — the same source handleTouchStart
     * already counts — so SSR cards swipe without any registered data.
     */
    _getCardImages(element) {
      const productData = this.getProductDataFromElement(element);
      if (productData?.images?.length) {
        // Photo cap: the data can list more photos than the card rendered.
        this._syncCardSlides(element, productData);
        return productData.images;
      }
      const slideImgs = element.querySelectorAll('.lumistelle-image-track .lumistelle-image-slide img');
      if (!slideImgs.length) return null;
      return Array.from(slideImgs).map(img => ({
        id: null,
        src: img.dataset.src || img.currentSrc || img.src || '',
        srcset: img.dataset.srcset || img.getAttribute('srcset') || ''
      }));
    }

    /**
     * PHOTO CAP: a lite card renders its first N photos (theme setting
     * card_photo_cap) and carries data-photo-count when it left some out.
     * Her first photo swipe on the card fetches the full list (the same
     * fetch and cache the gallery uses); the missing photos are then
     * appended as lazy slides. Grid photo autoplay never calls this.
     */
    _expandCardPhotos(element) {
      if (!element || element._photosExpanded || element._photoExpansion) return;
      const track = element.querySelector('.lumistelle-image-track');
      const total = parseInt(element.dataset.photoCount, 10) || 0;
      if (!track || total <= track.children.length) return;

      const productId = parseInt(element.dataset.productId, 10);
      const features = window.LumistelleCarouselFeatures;
      let ready;
      if (this.variantData.has(productId)) {
        ready = Promise.resolve();
      } else if (features && typeof features._ensureProductData === 'function' && element.dataset.handle) {
        ready = features._ensureProductData(productId, element.dataset.handle);
      } else {
        return;
      }

      // Pending marker: changeImage waits on it instead of wrapping early.
      element._photoExpansion = ready
        .then(() => {
          this._syncCardSlides(element, this.getProductDataFromElement(element));
          element._photosExpanded = true;
        })
        .catch(err => console.warn('[Lumistelle] photo expansion failed:', err))
        .finally(() => { element._photoExpansion = null; });
    }

    /**
     * PHOTO CAP: append the slides the card did not render, so the photo
     * list and the track always agree (whichever loaded the data first:
     * her swipe, the gallery, or a full-card data island). Each new slide
     * clones the last rendered one, so class, sizes, alt and dimensions
     * match the server markup; the photo itself stays deferred (data-src).
     */
    _syncCardSlides(element, productData) {
      const track = element.querySelector('.lumistelle-image-track');
      const images = productData?.images;
      if (!track || !images || images.length <= track.children.length) return;
      const lastSlide = track.lastElementChild;
      const lastImg = lastSlide ? lastSlide.querySelector('img') : null;
      if (!lastImg) return;

      const fragment = document.createDocumentFragment();
      for (let i = track.children.length; i < images.length; i++) {
        const slide = lastSlide.cloneNode(false);
        const img = lastImg.cloneNode(false);
        img.removeAttribute('srcset');
        img.removeAttribute('loading');
        img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
        img.dataset.src = images[i].src;
        if (images[i].srcset) img.dataset.srcset = images[i].srcset;
        else img.removeAttribute('data-srcset');
        slide.appendChild(img);
        fragment.appendChild(slide);
      }
      track.appendChild(fragment);

      // Keep the usual two-ahead buffer from the photo on screen.
      const totalSlides = track.children.length;
      const current = element.currentImageIndex || 0;
      [current + 1, current + 2].forEach(idx => {
        const img = track.children[idx % totalSlides]?.querySelector('img[data-src]');
        if (!img) return;
        img.src = img.dataset.src;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.removeAttribute('data-src');
        img.removeAttribute('data-srcset');
      });
    }

    changeImage(element, direction, isTouch = false) {
      const images = this._getCardImages(element);
      if (!images || images.length <= 1) return;

      let newIndex = (element.currentImageIndex || 0) + direction;

      // Photo cap: while the rest of the photos are on their way, a move
      // past either end waits for them instead of wrapping early.
      if (element._photoExpansion && (newIndex < 0 || newIndex >= images.length)) {
        this.slideToImage(element, element.currentImageIndex || 0, true, isTouch);
        element._photoExpansion.then(() => this.changeImage(element, direction, isTouch));
        return;
      }

      // Loop gallery
      if (newIndex < 0) newIndex = images.length - 1;
      if (newIndex >= images.length) newIndex = 0;

      this.slideToImage(element, newIndex, true, isTouch);
    }

    slideToImage(element, index, animate = true, isTouch = false) {
      const productData = this.getProductDataFromElement(element);
      const images = this._getCardImages(element);
      // LITE CARD: currentImageIndex is hydration-time state — a never-swiped
      // lite card passes undefined here (e.g. the touchend snap-back), which
      // made images[index] fail the guard and silently skip the snap.
      // Default to slide 0, which is where an unswiped card visually is.
      if (index == null || Number.isNaN(index)) index = 0;
      if (!images || !images[index]) return;

      const imageTrack = element.querySelector('.lumistelle-image-track');
      
      if (imageTrack) {
        // --- Activate deferred images: target + next 2 ---
        const slides = imageTrack.children;
        const totalSlides = slides.length;
        const indicesToActivate = [
          index,
          (index + 1) % totalSlides,
          (index + 2) % totalSlides
        ];
        indicesToActivate.forEach(idx => {
          const img = slides[idx]?.querySelector('img[data-src]');
          if (img) {
            img.src = img.dataset.src;
            if (img.dataset.srcset) img.srcset = img.dataset.srcset;
            img.removeAttribute('data-src');
            img.removeAttribute('data-srcset');
          }
        });

        // --- GRID PATH: Animate the Track ---
        const targetTranslate = `translateX(-${index * 100}%)`;
        
        // === SWIPE PERF PHASE 2: Use touch-optimized timing for swipe gestures ===
        let transitionValue = 'none';
        if (animate) {
          if (isTouch) {
            // Touch swipes: fast and snappy (180ms)
            transitionValue = `transform ${this.config.touchAnimationDuration}ms ${this.config.touchAnimationEasing}`;
          } else {
            // Programmatic/autoplay: smooth and deliberate (400ms)
            transitionValue = 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
          }
        }
        
        imageTrack.style.transition = transitionValue;
        imageTrack.style.transform = targetTranslate;
      } else {
        // --- CAROUSEL PATH: Swap the Image Src ---
        const mediaContainer = element.querySelector('.lumistelle-carousel__product-media');
        if (element.isImageLoading) return;
        element.isImageLoading = true;
        mediaContainer.classList.add('is-loading');
        const visibleImg = element.querySelector('.lumistelle-carousel__product-image');
        const newImage = images[index];
        const tempImg = new Image();
        tempImg.src = newImage.src;
        tempImg.srcset = newImage.srcset;
        tempImg.sizes = visibleImg.sizes;
        tempImg.onload = () => {
          visibleImg.src = tempImg.src;
          visibleImg.srcset = tempImg.srcset;
          mediaContainer.classList.remove('is-loading');
          element.isImageLoading = false;
          this.preloadImages(element);
        };
        tempImg.onerror = () => {
          mediaContainer.classList.remove('is-loading');
          element.isImageLoading = false;
        };
      }
      
      element.currentImageIndex = index;
      // Update card-level image progress bar
      const totalImages = element.totalImages || images.length || 1;
      const progressFill = element.querySelector('.lumistelle-carousel__image-progress-fill');
      if (progressFill) {
        progressFill.style.width = `${((index + 1) / totalImages) * 100}%`;
      }
      this.preloadImages(element);

      // --- NEW: Sync Color Swatch ---
      const mediaId = images[index]?.id;
      const colorMap = productData?.mediaToColorMap;
      if (mediaId && colorMap) {
        const colorName = colorMap[mediaId];
        if (colorName) {
          const activeSwatch = element.querySelector(`.lumistelle-carousel__color[data-color="${colorName}"]`);
          if (activeSwatch && !activeSwatch.classList.contains('active')) {
            element.querySelectorAll('.lumistelle-carousel__color.active').forEach(el => el.classList.remove('active'));
            activeSwatch.classList.add('active');
            
            // Trigger a visual-only update of other options (like sizes) without changing the selected variant.
            const featuresModule = window.LumistelleCarouselFeatures;
            if (featuresModule && typeof featuresModule.updateAvailableOptions === 'function') {
              featuresModule.updateAvailableOptions(element);
            }
          }
        }
      }
      // --- END NEW ---
      this.updateImageNavArrows(element);
    }

    updateImageNavArrows(element, cachedElements = null) {
      // === PHASE 4: Use cached elements or fall back to querySelector ===
      const cached = cachedElements || this._getCardElements(element);
      
      const prevButton = cached.prevButton;
      const nextButton = cached.nextButton;

      if (!prevButton || !nextButton) {
        return;
      }
      
      const currentIndex = element.currentImageIndex || 0;
      const productData = this.getProductDataFromElement(element);
      const totalImages = productData?.images?.length || 0;
      
      prevButton.disabled = currentIndex <= 0;
      nextButton.disabled = currentIndex >= totalImages - 1;
    }

    // ======================================================================
    // INTENT-BASED PRELOADING WITH CANCELLATION (FINAL)
    // ======================================================================
    
    // === PHASE 3: Unified Visibility Observer ===

    /**
     * Sets up a single IntersectionObserver that handles multiple visibility-based features:
     * - Grid image autoplay (cycling images on visible cards)
     * - Image cycle autoplay (carousel image cycling)
     * - Mobile preloading (preload images for visible cards)
     * 
     * This consolidates 3 separate observers into 1, reducing scroll overhead.
     * 
     * @param {Object} instance - The carousel/grid instance
     */
    setupUnifiedVisibilityObserver(instance) {
      const observerKey = `unified-visibility-${instance.id}`;
      
      // Prevent duplicate observer setup
      if (this.observers.has(observerKey)) {
        return;
      }
      
      // Initialize visible cards set for this instance
      if (!this.visibleCards.has(instance.id)) {
        this.visibleCards.set(instance.id, new Set());
      }
      
      const visibleCardsSet = this.visibleCards.get(instance.id);
      const isGrid = this.state.instances[instance.id]?.isGrid;
      const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
      
      // Determine which features are enabled for this instance
      const features = {
        gridAutoplay: isGrid && instance.config.enable_grid_image_autoplay,
        imageCycle: !isGrid && instance.config.imageAutoplay,
        // Lite cards carry no product data, so the preloader has nothing to
        // load for them; only full cards get it (QA, October 2026).
        mobilePreload: isTouchDevice && instance.element.dataset.cardMode !== 'lite' // Desktop uses hover-based preloading instead
      };
      
      // If no visibility-based features are enabled, skip observer creation
      if (!features.gridAutoplay && !features.imageCycle && !features.mobilePreload) {
        return;
      }
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this._handleCardVisibilityChange(instance, entry, visibleCardsSet, features);
        });
      }, { 
        threshold: this.unifiedObserverThreshold,
        // Use rootMargin for slight preloading buffer
        rootMargin: '50px 0px'
      });
      
      // Observe all product cards in this instance
      const cards = instance.track?.querySelectorAll('.lumistelle-carousel__product');
      if (cards) {
        cards.forEach(card => observer.observe(card));
      }
      
      // Register observer in central registry
      this.registerObserver(observerKey, observer, 'unified-visibility', instance.id);
      
      // Store reference on instance for adding new cards later (infinite scroll)
      instance.unifiedVisibilityObserver = observer;
      
      // Store enabled features for reference
      instance.unifiedFeatures = features;
    }

    /**
     * Observe newly added cards (e.g., from infinite scroll) with the unified observer
     * @param {Object} instance - The carousel/grid instance
     * @param {NodeList|Array} newCards - New card elements to observe
     */
    observeNewCardsWithUnifiedObserver(instance, newCards) {
      if (!instance.unifiedVisibilityObserver || !newCards || newCards.length === 0) {
        return;
      }
      
      newCards.forEach(card => {
        instance.unifiedVisibilityObserver.observe(card);
      });
    }

    /**
     * Handle visibility change for a single card
     * Routes to appropriate feature handlers based on enabled features
     * 
     * @param {Object} instance - The carousel/grid instance
     * @param {IntersectionObserverEntry} entry - The intersection entry
     * @param {Set} visibleCardsSet - Set tracking visible cards for this instance
     * @param {Object} features - Object indicating which features are enabled
     */
    _handleCardVisibilityChange(instance, entry, visibleCardsSet, features) {
      const card = entry.target;
      const isVisible = entry.isIntersecting;
      
      if (isVisible) {
        // Card became visible
        // Check if user has manually interacted with this card's gallery
        const userInteracted = card.dataset.userInteracted === 'true';
        
        // Only add to visible set if user hasn't swiped (for autoplay purposes)
        if (!userInteracted) {
          visibleCardsSet.add(card);
        }
        
        // === Feature: Image Cycle Autoplay (Carousel) ===
        // Skip if user has swiped this card's gallery
        if (features.imageCycle && !userInteracted) {
          this._startImageCycleForCard(instance, card);
        }
        
        // === Feature: Mobile Preload ===
        if (features.mobilePreload) {
          this._schedulePreloadForCard(instance, card);
        }
        
        // Note: Grid autoplay uses the visibleCardsSet directly in its timer callback
        // so no additional action needed here for that feature
        
      } else {
        // Card became hidden
        visibleCardsSet.delete(card);
        
        // === Feature: Image Cycle Autoplay (Carousel) ===
        if (features.imageCycle) {
          this._stopImageCycleForCard(instance, card);
        }
        
        // === Feature: Mobile Preload ===
        if (features.mobilePreload) {
          this._cancelPreloadForCard(instance, card);
        }
      }
    }

    /**
     * Start image cycle timer for a specific card
     * @param {Object} instance - The carousel/grid instance  
     * @param {HTMLElement} card - The card element
     */
    _startImageCycleForCard(instance, card) {
      // Don't start if already has timer
      if (card.imageCycleTimer) {
        return;
      }
      
      const delay = instance.config.imageAutoplayDelay || 4000;
      
      card.imageCycleTimer = setInterval(() => {
        // PHASE 2: Skip work if user is scrolling
        if (this.isScrolling) {
          return;
        }
        
        // Skip if card is being hovered
        if (card.dataset.isHovered) {
          return;
        }
        
        if (!this._photoCycleAllowed()) return;
        this._advanceCardWhenReady(card);
      }, delay);
      
      // Register timer for tracking
      this.registerTimer(instance.id, card.imageCycleTimer, 'image-cycle', card);
    }

    /**
     * Stop image cycle timer for a specific card
     * @param {Object} instance - The carousel/grid instance
     * @param {HTMLElement} card - The card element
     */
    _stopImageCycleForCard(instance, card) {
      if (!card.imageCycleTimer) {
        return;
      }
      
      const timerId = card.imageCycleTimer;
      clearInterval(timerId);
      card.imageCycleTimer = null;
      
      // Remove from timer registry
      this.removeTimerFromRegistry(instance.id, timerId, 'image-cycle');
    }

    /**
     * Permanently stop image autoplay for a card after user swipe interaction
     * Called when user manually swipes through a product's image gallery on mobile
     * Prevents autoplay from resuming even when card re-enters viewport
     * 
     * @param {Object} instance - The carousel/grid instance
     * @param {HTMLElement} card - The card element
     */
    _permanentlyDisableAutoplayForCard(instance, card) {
      // Only applies on mobile/touch devices
      const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
      if (!isTouchDevice) return;
      
      // Mark the card as user-interacted (prevents future autoplay)
      card.dataset.userInteracted = 'true';
      
      // Stop any existing autoplay timer for this card
      this._stopImageCycleForCard(instance, card);
      
      // Remove from visible cards set so grid autoplay ignores it
      const visibleCardsSet = this.visibleCards.get(instance.id);
      if (visibleCardsSet) {
        visibleCardsSet.delete(card);
      }
    }

    /**
     * Schedule preload for a specific card (mobile)
     * @param {Object} instance - The carousel/grid instance
     * @param {HTMLElement} card - The card element
     */
    _schedulePreloadForCard(instance, card) {
      // PHASE 2: Skip if scrolling
      if (this.isScrolling) {
        return;
      }
      
      // Clear existing preload timer if any
      if (card.preloadTimer) {
        const existingTimerId = card.preloadTimer;
        clearTimeout(existingTimerId);
        this.removeTimerFromRegistry(instance.id, existingTimerId, 'preload');
        card.preloadTimer = null;
      }
      
      const timerId = setTimeout(() => {
        // PHASE 2: Double-check scroll state
        if (this.isScrolling) {
          this.removeTimerFromRegistry(instance.id, timerId, 'preload');
          card.preloadTimer = null;
          return;
        }
        
        this.preloadImages(card);
        
        // Remove from registry when timer fires
        this.removeTimerFromRegistry(instance.id, timerId, 'preload');
        card.preloadTimer = null;
      }, 400);
      
      card.preloadTimer = timerId;
      
      // Register timer for tracking
      this.registerTimer(instance.id, timerId, 'preload', card);
    }

    /**
     * Cancel preload for a specific card
     * @param {Object} instance - The carousel/grid instance
     * @param {HTMLElement} card - The card element
     */
    _cancelPreloadForCard(instance, card) {
      if (card.preloadTimer) {
        const timerId = card.preloadTimer;
        clearTimeout(timerId);
        this.removeTimerFromRegistry(instance.id, timerId, 'preload');
        card.preloadTimer = null;
      }
      
      // Also cancel any in-progress preloading
      this.cancelPreloading(instance, card);
    }

    // === PHASE 4: Card Element Caching ===

    /**
     * Cache all frequently-accessed DOM elements for a product card
     * Called once per card after HTML structure is created
     * 
     * @param {HTMLElement} cardElement - The product card element
     * @returns {Object} The cached element references
     */
    _cacheCardElements(cardElement) {
      const cache = {
        // Product info elements
        titleLink: cardElement.querySelector('.lumistelle-carousel__product-title a'),
        vendor: cardElement.querySelector('.lumistelle-carousel__product-vendor'),
        
        // Rating elements
        ratingContainer: cardElement.querySelector('.lumistelle-carousel__product-rating'),
        starsContainer: cardElement.querySelector('.lumistelle-carousel__stars'),
        reviewsCount: cardElement.querySelector('.lumistelle-carousel__reviews-count'),
        
        // Price elements
        priceContainer: cardElement.querySelector('.lumistelle-carousel__product-price'),
        currentPrice: cardElement.querySelector('.lumistelle-carousel__price--current'),
        comparePrice: cardElement.querySelector('.lumistelle-carousel__price--compare'),
        discount: cardElement.querySelector('.lumistelle-carousel__discount'),
        stockStatus: cardElement.querySelector('.lumistelle-stock-status'),
        
        // Badge elements
        badgeContainer: cardElement.querySelector('.lumistelle-carousel__badges'),
        materialBadgeContainer: cardElement.querySelector('.lumistelle-carousel__material-badge'),
        
        // Variant elements
        variantsContainer: cardElement.querySelector('.lumistelle-carousel__variants'),
        colorGroup: cardElement.querySelector('.lumistelle-carousel__variants-group--color'),
        colorsContainer: cardElement.querySelector('.lumistelle-carousel__colors'),
        colorLabel: cardElement.querySelector('.lumistelle-carousel__variants-group--color .lumistelle-carousel__variants-label'),
        sizeGroup: cardElement.querySelector('.lumistelle-carousel__variants-group--size'),
        sizesContainer: cardElement.querySelector('.lumistelle-carousel__sizes'),
        sizeLabel: cardElement.querySelector('.lumistelle-carousel__variants-group--size .lumistelle-carousel__variants-label'),
        
        // Action elements
        quickAddButton: cardElement.querySelector('.lumistelle-carousel__quick-add'),
        quickAddText: cardElement.querySelector('.lumistelle-carousel__quick-add-text'),
        quickViewButton: cardElement.querySelector('.lumistelle-carousel__quick-view'),
        wishlistButton: cardElement.querySelector('.lumistelle-carousel__wishlist'),
        
        // Media elements
        mediaContainer: cardElement.querySelector('.lumistelle-carousel__product-media'),
        imageProgressBar: cardElement.querySelector('.lumistelle-carousel__image-progress'),
        imageProgressFill: cardElement.querySelector('.lumistelle-carousel__image-progress-fill'),
        viewProductLink: cardElement.querySelector('.lumistelle-carousel__view-product'),
        imageTrack: cardElement.querySelector('.lumistelle-image-track'),
        productLink: cardElement.querySelector('.lumistelle-carousel__product-link'),
        productImage: cardElement.querySelector('.lumistelle-carousel__product-image'),
        
        // Navigation elements
        prevButton: cardElement.querySelector('.lumistelle-carousel__image-nav--prev'),
        nextButton: cardElement.querySelector('.lumistelle-carousel__image-nav--next')
      };
      
      // Store in WeakMap
      this.cardElementCache.set(cardElement, cache);
      
      return cache;
    }

    /**
     * Get cached element references for a product card
     * Creates cache if it doesn't exist (lazy caching)
     * 
     * @param {HTMLElement} cardElement - The product card element
     * @returns {Object} The cached element references
     */
    _getCardElements(cardElement) {
      // Return existing cache if available
      let cache = this.cardElementCache.get(cardElement);
      
      if (cache) {
        return cache;
      }
      
      // Create cache if not exists (lazy caching)
      return this._cacheCardElements(cardElement);
    }

    /**
     * Invalidate cache for a specific card (call when card HTML structure changes)
     * @param {HTMLElement} cardElement - The product card element
     */
    _invalidateCardCache(cardElement) {
      this.cardElementCache.delete(cardElement);
    }

    setupPreloadingListeners(instance) {
      // PHASE 3 HOT FIX: Prevent duplicate setup
      if (instance.preloadListenersSetup) {
        return;
      }

      const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;

      if (isTouchDevice) {
        this.setupMobilePreloading(instance);
      } else {
        this.setupDesktopPreloading(instance);
      }

      instance.preloadListenersSetup = true;
    }

    setupDesktopPreloading(instance) {
      // PHASE 3 HOT FIX: Prevent duplicate listener registration
      if (instance.desktopPreloadHandlers) {
        return;
      }

      const handleMouseOver = (e) => {
        // === PHASE 2: Skip preload scheduling if user is scrolling ===
        if (this.isScrolling) {
          return;
        }
        // === END PHASE 2 ===
        
        // PHASE 5 HOT FIX: Clear existing timer if present
        if (instance.preloadTimer) {
          const timerId = instance.preloadTimer;
          clearTimeout(timerId);
          
          // Use safe removal helper
          this.removeTimerFromRegistry(instance.id, timerId, 'preload');
          
          instance.preloadTimer = null;
        }
        
        const productEl = e.target.closest('.lumistelle-carousel__product');
        if (productEl) {
          const timerId = setTimeout(() => {
            // === PHASE 2: Double-check scroll state before preloading ===
            if (this.isScrolling) {
              this.removeTimerFromRegistry(instance.id, timerId, 'preload');
              instance.preloadTimer = null;
              return;
            }
            // === END PHASE 2 ===
            
            this.preloadImages(productEl);
            
            // PHASE 5 HOT FIX #2: Remove from registry when timer fires
            this.removeTimerFromRegistry(instance.id, timerId, 'preload');
            
            instance.preloadTimer = null;
          }, 250);
          
          instance.preloadTimer = timerId;
          
          // PHASE 5: Register preload timer
          this.registerTimer(instance.id, timerId, 'preload');
        }
      };

      const handleMouseOut = (e) => {
        // PHASE 5 HOT FIX: Clear timer and remove from registry
        if (instance.preloadTimer) {
          const timerId = instance.preloadTimer;
          clearTimeout(timerId);
          
          // Use safe removal helper
          this.removeTimerFromRegistry(instance.id, timerId, 'preload');
          
          instance.preloadTimer = null;
        }
        
        const productEl = e.target.closest('.lumistelle-carousel__product');
        if (productEl) {
          this.cancelPreloading(instance, productEl);
        }
      };

      instance.track.addEventListener('mouseover', handleMouseOver);
      this.registerEventListener(instance.id, instance.track, 'mouseover', handleMouseOver);

      instance.track.addEventListener('mouseout', handleMouseOut);
      this.registerEventListener(instance.id, instance.track, 'mouseout', handleMouseOut);
      
      // Store handlers for cleanup
      instance.desktopPreloadHandlers = { handleMouseOver, handleMouseOut };
    }

    setupMobilePreloading(instance) {
      // === PHASE 3: Unified observer handles visibility-based preloading ===
      // Preload scheduling is handled by _handleCardVisibilityChange via _schedulePreloadForCard
      // This method is now a no-op for mobile, kept for API compatibility
      // No dedicated observer needed - unified observer handles this
    }

    preloadImages(element) {
      // Cards with a photo track already load two slides ahead (slideToImage);
      // a separate 1200px copy here would be a second download of each photo.
      if (element.querySelector('.lumistelle-image-track')) return;
      const productData = this.getProductDataFromElement(element);
      const images = productData?.images;
      if (!images || images.length <= 1) return;

      const instance = this.getInstance(element);
      if (!instance) return;

      // === LITE CARD: per-card preload state is created during hydration
      // (setupCardImages), which lite cards never run. A lite card acquires
      // variantData on first gallery open (_ensureProductData), which
      // un-shields this function — so initialize the state lazily rather
      // than assuming hydration happened.
      if (!element.preloadedImageUrls) element.preloadedImageUrls = new Set();
      const baseIndex = element.currentImageIndex || 0;

      const bufferSize = 2; // Preload next 2 images
      for (let i = 1; i <= bufferSize; i++) {
        const nextIndex = (baseIndex + i) % images.length;
        const imageUrl = images[nextIndex]?.src;

        if (imageUrl && !element.preloadedImageUrls.has(imageUrl)) {
          element.preloadedImageUrls.add(imageUrl); // Mark as "attempted" immediately

          const preloadImg = new Image();
          
          // Store the image object for potential cancellation
          if (!instance.preloadState.has(element)) {
            instance.preloadState.set(element, []);
          }
          instance.preloadState.get(element).push({ url: imageUrl, imgObj: preloadImg });

          preloadImg.src = imageUrl;
        }
      }
    }

    cancelPreloading(instance, element) {
      if (instance.preloadState.has(element)) {
        const activePreloads = instance.preloadState.get(element);
        activePreloads.forEach(preload => {
          if (preload.imgObj) {
            preload.imgObj.src = ''; // Abort download
          }
        });
        instance.preloadState.delete(element);
      }
    }

    cancelAllPreloading(instance) {
      // Guard: preloadState is created at the END of a successful init,
      // but delegated touchstart is live from engine boot — a touch on a
      // not-yet-initialized instance must be a no-op, not a TypeError.
      if (!instance || !instance.preloadState) return;
      if (instance.preloadState.size > 0) {
        for (const element of instance.preloadState.keys()) {
          this.cancelPreloading(instance, element);
        }
      }
    }

    // ======================================================================
    // PHASE 3: ENHANCED EVENT DELEGATION WITH CLEANUP
    // ======================================================================

    /**
     * Setup global event delegation with proper cleanup tracking
     * PHASE 3: Now uses bound references and registry system
     */
    setupEventDelegation() {
      // Prevent double-attachment
      if (this.globalListenersAttached) {
        return;
      }

      // Create bound references ONCE (prevents .bind() memory leak)
      this.boundHandlers.click = this.handleClick.bind(this);
      this.boundHandlers.mousedown = this.handleMouseDown.bind(this);
      this.boundHandlers.touchstart = this.handleTouchStart.bind(this);
      this.boundHandlers.touchmove = this.handleTouchMove.bind(this);
      this.boundHandlers.touchend = this.handleTouchEnd.bind(this);
      this.boundHandlers.resize = this.debounce(this.handleResize.bind(this), 250);

      // Attach document listeners
      document.addEventListener('click', this.boundHandlers.click);
      this.registerEventListener('global', document, 'click', this.boundHandlers.click);

      document.addEventListener('mousedown', this.boundHandlers.mousedown);
      this.registerEventListener('global', document, 'mousedown', this.boundHandlers.mousedown);

      document.addEventListener('touchstart', this.boundHandlers.touchstart, { passive: true });
      this.registerEventListener('global', document, 'touchstart', this.boundHandlers.touchstart, { passive: true });

      document.addEventListener('touchmove', this.boundHandlers.touchmove, { passive: true });
      this.registerEventListener('global', document, 'touchmove', this.boundHandlers.touchmove, { passive: true });

      document.addEventListener('touchend', this.boundHandlers.touchend);
      this.registerEventListener('global', document, 'touchend', this.boundHandlers.touchend);

      // Attach window listeners
      window.addEventListener('resize', this.boundHandlers.resize);
      this.registerEventListener('global', window, 'resize', this.boundHandlers.resize);

      // === PHASE 2: Add passive scroll listener for timer management ===
      this.boundHandlers.scroll = this._handleScrollEvent.bind(this);
      window.addEventListener('scroll', this.boundHandlers.scroll, { passive: true });
      this.registerEventListener('global', window, 'scroll', this.boundHandlers.scroll, { passive: true });
      // === END PHASE 2 ===

      this.globalListenersAttached = true;
    }

    /**
     * PHASE 3: Clean up all global event listeners
     * Removes listeners from document and window
     */
    cleanupGlobalListeners() {
      if (!this.globalListenersAttached) {
        return;
      }

      // Remove document listeners
      if (this.boundHandlers.click) {
        document.removeEventListener('click', this.boundHandlers.click);
      }
      if (this.boundHandlers.mousedown) {
        document.removeEventListener('mousedown', this.boundHandlers.mousedown);
      }
      if (this.boundHandlers.touchstart) {
        document.removeEventListener('touchstart', this.boundHandlers.touchstart, { passive: true });
      }
      if (this.boundHandlers.touchmove) {
        document.removeEventListener('touchmove', this.boundHandlers.touchmove, { passive: true });
      }
      if (this.boundHandlers.touchend) {
        document.removeEventListener('touchend', this.boundHandlers.touchend);
      }

      // Remove window listeners
      if (this.boundHandlers.resize) {
        window.removeEventListener('resize', this.boundHandlers.resize);
      }
      if (this.boundHandlers.beforeunload) {
        window.removeEventListener('beforeunload', this.boundHandlers.beforeunload);
      }
      // === PHASE 2: Remove scroll listener ===
      if (this.boundHandlers.scroll) {
        window.removeEventListener('scroll', this.boundHandlers.scroll, { passive: true });
      }
      // Clear scroll state
      if (this.scrollEndTimeout) {
        clearTimeout(this.scrollEndTimeout);
        this.scrollEndTimeout = null;
      }
      this.isScrolling = false;
      this._scrollRafPending = false;
      // === END PHASE 2 ===

      // Clear registry
      this.removeInstanceEventListeners('global');

      // Clear bound handler references
      Object.keys(this.boundHandlers).forEach(key => {
        this.boundHandlers[key] = null;
      });

      this.globalListenersAttached = false;
    }

    handleClick(e) {
      const productEl = e.target.closest('.lumistelle-carousel__product');
      const carouselEl = e.target.closest('.lumistelle-carousel');

      // --- DRAG vs. CLICK PREVENTION ---
      const instanceFromClick = carouselEl ? this.getInstance(carouselEl) : null;
      if (instanceFromClick && instanceFromClick.hasDragged) {
        instanceFromClick.hasDragged = false; // Reset flag
        e.preventDefault(); // Stop the click
        return;
      }
      // --- END PREVENTION ---

      // Let View Product link navigate natively
      if (e.target.closest('.lumistelle-carousel__view-product')) {
        return;
      }

      // --- Universal Feature Click Handling ---
      // If the click is on any product card feature, delegate to the features module first.
      // This now works for both grid and carousel products.
      if (productEl && window.LumistelleCarouselFeatures) {
        window.LumistelleCarouselFeatures.handleClick(e);
      }

      // --- Carousel-Specific Navigation Handling ---
      // Only proceed with carousel logic if we are inside a carousel.
      if (!carouselEl) return;
      
      const instance = this.getInstance(carouselEl);
      if (!instance) return;

      const arrowOrDot = e.target.closest('.lumistelle-carousel__arrow, .lumistelle-carousel__dot');
      if (arrowOrDot) {
        this.stopAutoplay(instance); // Stop autoplay on manual navigation

        if (arrowOrDot.matches('.lumistelle-carousel__arrow--prev')) {
          this.slidePrev(instance);
        } else if (arrowOrDot.matches('.lumistelle-carousel__arrow--next')) {
          this.slideNext(instance);
        } else if (arrowOrDot.matches('.lumistelle-carousel__dot')) {
          const dotIndex = parseInt(arrowOrDot.dataset.index, 10);
          const productsPerView = Math.floor(this.getProductsPerView(instance));
          const state = this.state.instances[instance.id];
          const totalProducts = state.products.length;
          const totalSlides = Math.ceil(totalProducts / productsPerView);
          
          let targetIndex;
          if (dotIndex === totalSlides - 1) {
            targetIndex = Math.max(0, totalProducts - productsPerView);
          } else {
            targetIndex = dotIndex * productsPerView;
          }
          
          this.slideTo(instance, targetIndex);
        }
      }
    }

    handleTouchStart(e) {
      const instanceEl = e.target.closest('.lumistelle-carousel, .lumistelle-grid-section');
      if (!instanceEl) return;

      const instance = this.instances.get(instanceEl.id);
      if (!instance) return;

      this.cancelAllPreloading(instance);
      const touch = e.touches[0];
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchStartTime = Date.now();
      this.state.ui.isSwiping = false;
      this.state.ui.touchAction = null;

      // === PHASE 1: Clear any previous cached dimensions ===
      this.state.ui.cachedDimensions = null;

      const isGrid = instanceEl.classList.contains('lumistelle-grid-section');
      
      if (isGrid) {
        const productEl = e.target.closest('.lumistelle-carousel__product');
        const mediaEl = e.target.closest('.lumistelle-carousel__product-media');
        const imageTrack = productEl ? productEl.querySelector('.lumistelle-image-track') : null;
        
        if (mediaEl && imageTrack) {
          this.state.ui.touchAction = 'image-swipe';
          this.state.ui.activeProductEl = productEl;
          
          // === PHASE 1: Cache dimensions at touch start for image swipe ===
          const currentImageIndex = productEl.currentImageIndex || 0;
          const elementWidth = productEl.offsetWidth;
          
          this.state.ui.cachedDimensions = {
            elementWidth: elementWidth,
            imageTrack: imageTrack,
            // === FAST DRAG: Pre-calculate base translate for zero-overhead drag ===
            baseTranslate: -(currentImageIndex * elementWidth)
          };

          // === DEFERRED IMAGES: Activate adjacent slides before drag begins ===
          const slides = imageTrack.children;
          const totalSlides = slides.length;
          const adjacentIndices = [
            (currentImageIndex + 1) % totalSlides,
            (currentImageIndex - 1 + totalSlides) % totalSlides
          ];
          adjacentIndices.forEach(idx => {
            const img = slides[idx]?.querySelector('img[data-src]');
            if (img) {
              img.src = img.dataset.src;
              if (img.dataset.srcset) img.srcset = img.dataset.srcset;
              img.removeAttribute('data-src');
              img.removeAttribute('data-srcset');
            }
          });

          // Release a paused drag animation a missed touchend left behind.
          this._endTrackDrag(imageTrack);
          
          // === DRAG SPEED FIX: Add dragging class to disable CSS transitions ===
          imageTrack.classList.add('is-dragging');
        }
      } else {
        this.stopAutoplay(instance);
        this.state.ui.touchAction = 'track-swipe';
        this.state.ui.activeCarousel = instance.id;
        
        // === PHASE 1: Cache dimensions at touch start for track swipe ===
        const state = this.state.instances[instance.id];
        const productsPerView = this.getProductsPerView(instance);
        const productWidth = this.calculateProductWidth(instance);
        const maxIndex = Math.max(0, state.products.length - productsPerView);
        
        this.state.ui.cachedDimensions = {
          productWidth: productWidth,
          productsPerView: productsPerView,
          maxIndex: maxIndex,
          minTranslate: -(maxIndex * productWidth),
          maxTranslate: 0,
          track: instance.track,
          currentIndex: state.currentIndex,
          // === FAST DRAG: Pre-calculate base translate for zero-overhead drag ===
          baseTranslate: -(state.currentIndex * productWidth)
        };

        // A touchend that never arrived can leave a paused drag animation on
        // the strip; release it before this gesture starts.
        this._endTrackDrag(instance.track);
        
        // === DRAG SPEED FIX: Add dragging class to disable CSS transitions ===
        instance.track.classList.add('is-dragging');

        // === MOMENTUM: init velocity tracking + cancel any in-flight glide ===
        if (instance._momentumAnim) {
          // Stop the compositor glide exactly where it is: read the live
          // position, pin it inline, then cancel the animation.
          const livePx = this._readTrackTranslate(instance);
          instance.track.style.transform = 'translateX(' + livePx + 'px)';
          instance._momentumAnim.cancel();
          instance._momentumAnim = null;
          this.state.ui.cachedDimensions.baseTranslate = livePx;
          // Finalize never ran, so adopt the interrupted position into state:
          // isAnimating is still true and currentIndex still points pre-glide.
          const liveIndex = Math.max(0, Math.min(Math.round(-livePx / productWidth), maxIndex));
          state.currentIndex = liveIndex;
          this.state.ui.cachedDimensions.currentIndex = liveIndex;
          state.isAnimating = false;
        }
        this.state.ui.vEMA = 0;
        this.state.ui.vLastX = touch.clientX;
        this.state.ui.vLastT = this.touchStartTime;
      }
    }

    handleTouchMove(e) {
      // === FAST DRAG: Minimal overhead path ===
      // Exit immediately if not in a drag action
      const touchAction = this.state.ui.touchAction;
      if (!touchAction) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - this.touchStartX;
      const deltaY = touch.clientY - this.touchStartY;

      // === Direction detection (only on first significant movement) ===
      if (!this.state.ui.isSwiping) {
        const absDeltaX = Math.abs(deltaX);
        const absDeltaY = Math.abs(deltaY);
        
        // Need at least 3px movement to determine direction
        if (absDeltaX < 3 && absDeltaY < 3) return;
        
        // If clearly vertical scrolling, abort horizontal swipe
        if (absDeltaY > absDeltaX + 3) {
          this.state.ui.touchAction = null;
          const cached = this.state.ui.cachedDimensions;
          if (cached) {
            const track = cached.track || cached.imageTrack;
            if (track) track.classList.remove('is-dragging');
          }
          return;
        }
        
        // Commit to horizontal swiping
        this.state.ui.isSwiping = true;

        // Photo cap: her first photo swipe on a card fetches the rest of its
        // photos, starting while her finger is still moving.
        if (this.state.ui.touchAction === 'image-swipe') {
          this._expandCardPhotos(this.state.ui.activeProductEl);
        }

        // A sideways swipe on a carousel strip stops its autoplay for good.
        if (this.state.ui.touchAction === 'track-swipe') {
          const swiped = this.instances.get(this.state.ui.activeCarousel);
          if (swiped) swiped.autoplayStoppedByUser = true;
        }
      }

      // === FAST DRAG: Direct transform update with pre-calculated base ===
      const cached = this.state.ui.cachedDimensions;
      if (!cached) return;
      
      const track = cached.track || cached.imageTrack;
      if (!track) return;

      // === MOMENTUM: sample EMA velocity (track-swipe only) ===
      if (this.state.ui.touchAction === 'track-swipe') {
        const nowT = Date.now();
        const dt = nowT - this.state.ui.vLastT;
        if (dt > 0) {
          const instV = (touch.clientX - this.state.ui.vLastX) / dt; // px/ms, signed
          this.state.ui.vEMA = (0.3 * instV) + (0.7 * (this.state.ui.vEMA || 0));
          this.state.ui.vLastX = touch.clientX;
          this.state.ui.vLastT = nowT;
        }
      }

      // Single addition - no multiplication, no property lookups
      let translateX = cached.baseTranslate + deltaX;

      // === MOMENTUM: rubber-band resistance past the ends (track-swipe only) ===
      if (this.state.ui.touchAction === 'track-swipe' && cached.minTranslate !== undefined) {
        if (translateX > cached.maxTranslate) {
          translateX = cached.maxTranslate + (translateX - cached.maxTranslate) * 0.35;
        } else if (translateX < cached.minTranslate) {
          translateX = cached.minTranslate - (cached.minTranslate - translateX) * 0.35;
        }
      }

      this._dragTrackTo(track, cached, translateX);
    }

    handleTouchEnd(e) {
      const action = this.state.ui.touchAction;
      
      // === PHASE 1: Get cached dimensions before any checks ===
      const cached = this.state.ui.cachedDimensions;

      // Hand a paused drag animation back to the inline style first, so the
      // release below reads and moves from the finger's position.
      if (cached) this._endTrackDrag(cached.track || cached.imageTrack);
      
      if (!action || !this.state.ui.isSwiping) {
        // === DRAG SPEED FIX: Clean up dragging class even on early exit ===
        const cached = this.state.ui.cachedDimensions;
        const cachedTrack = cached?.track || cached?.imageTrack;
        if (cachedTrack) {
          cachedTrack.classList.remove('is-dragging');
        }
        
        this.state.ui.isSwiping = false;
        this.state.ui.touchAction = null;
        this.state.ui.cachedDimensions = null;
        return;
      }

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - this.touchStartX;
      const touchDuration = Date.now() - this.touchStartTime;
      const velocity = Math.abs(deltaX) / touchDuration;

      // === SWIPE PERF PHASE 1: Force style flush before animation ===
      // The track currently has 'transition: none' from handleTouchMove.
      // We must ensure the browser commits this before we re-enable transitions.
      // Double RAF guarantees the style is flushed to the rendering pipeline.
      
      if (action === 'track-swipe') {
        const instance = this.instances.get(this.state.ui.activeCarousel);
        const state = this.state.instances[instance.id];
        
        let maxIndex;
        if (cached && cached.maxIndex !== undefined) {
          maxIndex = cached.maxIndex;
        } else {
          const productsPerView = this.getProductsPerView(instance);
          maxIndex = Math.max(0, state.products.length - productsPerView);
        }

        // === MOMENTUM PATH (gated on config.momentum) ===
        if (instance.config.momentum) {
          this._releaseWithMomentum(instance, cached, maxIndex);
        } else {
          // --- Legacy threshold + CSS settle (old island carousels) ---
          const meetsThreshold = Math.abs(deltaX) > this.config.swipeThreshold || velocity > this.config.velocityThreshold;
          const canMoveLeft = deltaX > 0 && state.currentIndex > 0;
          const canMoveRight = deltaX < 0 && state.currentIndex < maxIndex;
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (meetsThreshold && canMoveLeft) {
                this.slidePrev(instance, true); // true = touch-initiated
              } else if (meetsThreshold && canMoveRight) {
                this.slideNext(instance, true); // true = touch-initiated
              } else {
                this.updateTrackPosition(instance, true, true); // animate=true, isTouch=true
              }
            });
          });
        }
        
      } else if (action === 'image-swipe') {
        const productEl = this.state.ui.activeProductEl;
        
        let elementWidth;
        if (cached && cached.elementWidth) {
          elementWidth = cached.elementWidth;
        } else {
          elementWidth = productEl.offsetWidth;
        }
        
        // === AUTOPLAY DISABLE: Stop autoplay for this card after user swipe ===
        const instanceEl = productEl.closest('.lumistelle-carousel, .lumistelle-grid-section');
        const instance = instanceEl ? this.getInstance(instanceEl) : null;
        if (instance) {
          this._permanentlyDisableAutoplayForCard(instance, productEl);
        }
        
        const meetsThreshold = Math.abs(deltaX) > elementWidth / 4 || velocity > this.config.velocityThreshold;
        const direction = deltaX < 0 ? 1 : -1;
        
        // === SWIPE PERF: Double RAF for guaranteed style flush ===
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (meetsThreshold) {
              this.changeImage(productEl, direction, true); // true = touch-initiated
            } else {
              this.slideToImage(productEl, productEl.currentImageIndex, true, true); // animate=true, isTouch=true
            }
          });
        });
      }

      // === DRAG SPEED FIX: Remove dragging class before animation ===
      // This must happen AFTER the double RAF queues the animation
      // The RAF will execute, set the proper transition, then animate
      const cachedTrack = cached?.track || cached?.imageTrack;
      if (cachedTrack) {
        cachedTrack.classList.remove('is-dragging');
      }
      
      // Reset state and clear cache immediately (animation happens async)
      this.state.ui.touchAction = null;
      this.state.ui.activeCarousel = null;
      this.state.ui.activeProductEl = null;
      this.state.ui.cachedDimensions = null;
      
      // === SWIPE PERF: Sync flag reset with animation completion ===
      // Use touch animation duration + small buffer instead of arbitrary 50ms
      const resetDelay = this.config.touchAnimationDuration + 50;
      setTimeout(() => { this.state.ui.isSwiping = false; }, resetDelay);
    }

    handleDrag(instance, deltaX) {
      // === PHASE 1: Use cached dimensions instead of reading from DOM ===
      const cached = instance.cachedDragDimensions;
      
      // Defensive fallback: if cache missing, calculate once (shouldn't happen)
      let productWidth, minTranslate, maxTranslate;
      
      if (cached) {
        productWidth = cached.productWidth;
        minTranslate = cached.minTranslate;
        maxTranslate = cached.maxTranslate;
      } else {
        // Fallback path - should not execute in normal flow
        const state = this.state.instances[instance.id];
        const productsPerView = this.getProductsPerView(instance);
        productWidth = this.calculateProductWidth(instance);
        const maxIndex = Math.max(0, state.products.length - productsPerView);
        minTranslate = -(maxIndex * productWidth);
        maxTranslate = 0;
      }
      // === END PHASE 1 ===
      
      // Calculate the intended translate position
      let currentTranslate = instance.dragStartTranslate + deltaX;
      
      // Add resistance at boundaries
      if (currentTranslate > maxTranslate) {
        const overscroll = currentTranslate - maxTranslate;
        currentTranslate = maxTranslate + (overscroll * 0.3);
      } else if (currentTranslate < minTranslate) {
        const overscroll = minTranslate - currentTranslate;
        currentTranslate = minTranslate - (overscroll * 0.3);
      }
      
      instance.track.style.transform = `translateX(${currentTranslate}px)`;
    }

    handleResize() {
      this.instances.forEach(instance => {
        const state = this.state.instances[instance.id];
        // Only run layout updates on carousels, not grids.
        if (state && !state.isGrid) {
          this.updateLayout(instance);
        }
      });
    }

    handleMouseDown(e) {
      if (e.button !== 0) return;
      const carouselEl = e.target.closest('.lumistelle-carousel');
      if (!carouselEl) return;

      const instance = this.getInstance(carouselEl);
      if (!instance || !instance.track) return;

      e.preventDefault();

      instance.isMouseDown = true;
      instance.startX = e.clientX;
      instance.hasDragged = false;

      // Get the current translate value from the transform property
      const transformMatrix = window.getComputedStyle(instance.track).transform;
      if (transformMatrix !== 'none') {
        instance.dragStartTranslate = parseFloat(transformMatrix.split(',')[4]);
      } else {
        instance.dragStartTranslate = 0;
      }

      // === PHASE 1: Cache dimensions at mouse down for drag ===
      const state = this.state.instances[instance.id];
      const productsPerView = this.getProductsPerView(instance);
      const productWidth = this.calculateProductWidth(instance);
      const maxIndex = Math.max(0, state.products.length - productsPerView);
      
      instance.cachedDragDimensions = {
        productWidth: productWidth,
        productsPerView: productsPerView,
        maxIndex: maxIndex,
        minTranslate: -(maxIndex * productWidth),
        maxTranslate: 0
      };
      // === END PHASE 1 ===

      instance.element.classList.add('is-dragging');
      instance.track.style.transition = 'none';

      const handleMouseMove = this.handleMouseMove.bind(this, instance);
      const handleMouseUp = this.handleMouseUp.bind(this, instance);

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      instance.unboundMouseHandlers = { handleMouseMove, handleMouseUp };
    }

    handleMouseMove(instance, e) {
      if (!instance.isMouseDown) return;

      const deltaX = e.clientX - instance.startX;

      // Set hasDragged flag if movement exceeds a small threshold
      if (Math.abs(deltaX) > 5 && !instance.hasDragged) {
        instance.hasDragged = true;
      }

      // Use rAF for performance
      cancelAnimationFrame(instance.rafId);
      instance.rafId = requestAnimationFrame(() => {
        this.handleDrag(instance, deltaX);
      });
    }

    handleMouseUp(instance, e) {
      if (!instance.isMouseDown) return;

      instance.isMouseDown = false;
      instance.element.classList.remove('is-dragging');
      cancelAnimationFrame(instance.rafId);

      // Remove the global listeners
      if (instance.unboundMouseHandlers) {
        document.removeEventListener('mousemove', instance.unboundMouseHandlers.handleMouseMove);
        document.removeEventListener('mouseup', instance.unboundMouseHandlers.handleMouseUp);
      }
      
      // If no significant drag, it was a click, snap back and allow click to proceed
      if (!instance.hasDragged) {
        instance.cachedDragDimensions = null; // === PHASE 1: Clear cache ===
        this.updateTrackPosition(instance, true);
        return;
      }
      
      const deltaX = e.clientX - instance.startX;
      const state = this.state.instances[instance.id];
      
      // === PHASE 1: Use cached dimensions with fallback ===
      const cached = instance.cachedDragDimensions;
      let productWidth, maxIndex;
      
      if (cached) {
        productWidth = cached.productWidth;
        maxIndex = cached.maxIndex;
      } else {
        // Fallback path - should not execute in normal flow
        productWidth = this.calculateProductWidth(instance);
        const productsPerView = this.getProductsPerView(instance);
        maxIndex = Math.max(0, state.products.length - productsPerView);
      }
      
      // Clear cache after use
      instance.cachedDragDimensions = null;
      // === END PHASE 1 ===
      
      // --- "Direct Snap-to-Grid" Logic ---
      const slidesMoved = Math.round(deltaX / productWidth);
      let newIndex = state.currentIndex - slidesMoved;
      newIndex = Math.max(0, Math.min(newIndex, maxIndex));
      
      this.slideTo(instance, newIndex, true);
    }

    // ======================================================================
    // NAVIGATION & LAYOUT
    // ======================================================================
    slideTo(instance, index, animate = true, isTouch = false) {
      // PHASE 3 HOT FIX #2: Safety check
      if (!instance || !instance.track) {
        return;
      }
      
      const state = this.state.instances[instance.id];
      if (!state || !state.products) {
        return;
      }
      
      const productsPerView = this.getProductsPerView(instance);
      const maxIndex = Math.max(0, state.products.length - productsPerView);
      
      index = Math.max(0, Math.min(index, maxIndex));
      state.currentIndex = index;
      
      this.updateTrackPosition(instance, animate, isTouch);
      this.updateNavigation(instance);
    }

    slideNext(instance, isTouch = false) {
      // PHASE 3 HOT FIX #2: Safety check
      const state = this.state.instances[instance.id];
      if (!state || !state.products) {
        return;
      }
      
      const productsPerView = Math.floor(this.getProductsPerView(instance));
      const totalProducts = state.products.length;
      const isMulticol = instance.element.classList.contains('lumistelle-grid--mobile-multicol') && window.innerWidth < 990;
      const step = isMulticol ? 1 : productsPerView;
      const maxIndex = Math.max(0, totalProducts - productsPerView);

      if (state.currentIndex < maxIndex) {
        const nextIndex = Math.min(state.currentIndex + step, maxIndex);
        this.slideTo(instance, nextIndex, true, isTouch);
      }
    }

    slidePrev(instance, isTouch = false) {
      // PHASE 3 HOT FIX #2: Safety check
      const state = this.state.instances[instance.id];
      if (!state || !state.products) {
        return;
      }
      
      const productsPerView = Math.floor(this.getProductsPerView(instance));
      const isMulticol = instance.element.classList.contains('lumistelle-grid--mobile-multicol') && window.innerWidth < 990;
      const step = isMulticol ? 1 : productsPerView;

      if (state.currentIndex > 0) {
        const prevIndex = Math.max(0, state.currentIndex - step);
        this.slideTo(instance, prevIndex, true, isTouch);
      }
    }

    slideByOne(instance, direction) {
      const state = this.state.instances[instance.id];
      const newIndex = state.currentIndex + direction;
      this.slideTo(instance, newIndex);
    }

    updateTrackPosition(instance, animate = true, isTouch = false) {
      const state = this.state.instances[instance.id];
      const productWidth = this.calculateProductWidth(instance);
      const translateX = this._snapPx(-(state.currentIndex * productWidth));
      
      // === SWIPE PERF PHASE 2: Use touch-optimized timing for swipe gestures ===
      let transitionValue = 'none';
      if (animate) {
        if (isTouch) {
          // Touch swipes: fast and snappy
          transitionValue = `transform ${this.config.touchAnimationDuration}ms ${this.config.touchAnimationEasing}`;
        } else {
          // Programmatic/autoplay: smooth and deliberate
          transitionValue = `transform ${this.config.animationDuration}ms ease`;
        }
      }
      
      instance.track.style.transition = transitionValue;
      instance.track.style.transform = `translateX(${translateX}px)`;
    }

    // ======================================================================
    // MOMENTUM (carousel track swipe) — paged, velocity-continuous
    // ======================================================================

    // Read the track's live X translate from its computed matrix (includes any
    // in-flight glide / resistance). Mirrors the mouse-path matrix read.
    _readTrackTranslate(instance) {
      const m = window.getComputedStyle(instance.track).transform;
      if (!m || m === 'none') return 0;
      const parts = m.replace(/matrix3?d?\(|\)/g, '').split(',').map(parseFloat);
      return parts.length === 16 ? (parts[12] || 0) : (parts[4] || 0);
    }

    // Resting positions land on whole device pixels. The carousel strip keeps
    // its own GPU layer permanently (lumistelle-carousel.css), and a layer
    // parked on a half pixel (202.5px cards) is resampled, which blurs it on
    // 3x screens.
    _snapPx(px) {
      const dpr = window.devicePixelRatio || 1;
      return Math.round(px * dpr) / dpr;
    }

    // Drag a strip (carousel or card photos) with a paused Web Animation, not an
    // inline style. An inline style is a DOM change on every touchmove, and
    // both Clarity feeds (clarity.js and its Shopify pixel) process each one:
    // ~11ms of main-thread work per drag frame at 4x CPU, ~5ms this way
    // (QA, October 2026). _endTrackDrag hands the live position back to the
    // inline style before release, so the momentum code is unchanged.
    _dragTrackTo(track, cached, x) {
      if (typeof track.animate !== 'function') {
        track.style.transform = 'translateX(' + x + 'px)';
        return;
      }
      if (!track._dragAnim) {
        // Linear from one screen width past each end, so rubber-banding stays in
        // range. Photo strips cache no ends: a finger can only travel one screen
        // width from where it started.
        const pad = window.innerWidth;
        const lo = cached.minTranslate !== undefined ? cached.minTranslate : cached.baseTranslate;
        const hi = cached.maxTranslate !== undefined ? cached.maxTranslate : cached.baseTranslate;
        track._dragFrom = lo - pad;
        track._dragSpan = (hi + pad) - track._dragFrom;
        track._dragAnim = track.animate(
          [{ transform: 'translateX(' + track._dragFrom + 'px)' },
           { transform: 'translateX(' + (track._dragFrom + track._dragSpan) + 'px)' }],
          { duration: 1000, easing: 'linear', fill: 'both' }
        );
        track._dragAnim.pause();
      }
      track._dragAnim.currentTime = ((x - track._dragFrom) / track._dragSpan) * 1000;
    }

    _endTrackDrag(track) {
      if (!track || !track._dragAnim) return;
      const livePx = this._readTrackTranslate({ track: track });
      track.style.transform = 'translateX(' + livePx + 'px)';
      track._dragAnim.cancel();
      track._dragAnim = null;
    }

    _releaseWithMomentum(instance, cached, maxIndex) {
      const FLICK_MIN    = 0.2;  // px/ms; below this, no throw — snap to nearest
      const PROJECT_MS   = 90;    // ms of velocity carried into the projection
      const MOMENTUM_MIN = 250;   // ms; glide duration floor
      const MOMENTUM_MAX = 400;   // ms; glide duration ceiling

      const state = this.state.instances[instance.id];
      const productWidth = cached.productWidth || this.calculateProductWidth(instance);
      const minT = cached.minTranslate;
      const maxT = cached.maxTranslate;

      const fromPx = this._readTrackTranslate(instance);
      const v0 = this.state.ui.vEMA || 0; // signed px/ms; negative = forward

      let landing = Math.abs(v0) >= FLICK_MIN ? (fromPx + v0 * PROJECT_MS) : fromPx;
      landing = Math.max(minT, Math.min(maxT, landing));
      let targetIndex = Math.round(-landing / productWidth);
      targetIndex = Math.max(0, Math.min(targetIndex, maxIndex));

      const toPx = this._snapPx(-(targetIndex * productWidth));
      const dist = toPx - fromPx;
      const absDist = Math.abs(dist);

      if (absDist < 0.5) {
        this._finalizeMomentum(instance, targetIndex, toPx);
        return;
      }

      // easeOutQuad opens at 2*dist/dur; set dur = 2*dist/|v0| to open at v0.
      let dur = Math.abs(v0) > 0 ? (2 * absDist / Math.abs(v0)) : MOMENTUM_MIN;
      dur = Math.max(MOMENTUM_MIN, Math.min(MOMENTUM_MAX, dur));

      if (typeof instance.track.animate !== 'function') {
        this._finalizeMomentum(instance, targetIndex, toPx);
        return;
      }

      state.isAnimating = true;
      instance.track.style.transition = 'none';

      // The glide runs on the compositor (Web Animations), so its frames cost
      // the main thread nothing (QA trace, October 2026). The end position is
      // pinned inline first, so nothing jumps when the animation ends.
      // cubic-bezier(0.5, 1, 0.89, 1) is easeOutQuad: it opens at 2*dist/dur,
      // which the duration above matches to the release velocity.
      instance.track.style.transform = 'translateX(' + toPx + 'px)';
      const anim = instance.track.animate(
        [{ transform: 'translateX(' + fromPx + 'px)' }, { transform: 'translateX(' + toPx + 'px)' }],
        { duration: dur, easing: 'cubic-bezier(0.5, 1, 0.89, 1)' }
      );
      instance._momentumAnim = anim;
      anim.onfinish = () => {
        if (instance._momentumAnim !== anim) return; // interrupted by a new touch
        instance._momentumAnim = null;
        this._finalizeMomentum(instance, targetIndex, toPx);
      };
    }

        _finalizeMomentum(instance, targetIndex, toPx) {
      instance._momentumAnim = null;
      const state = this.state.instances[instance.id];
      if (!state || !instance.track) return;
      instance.track.style.transition = 'none';
      instance.track.style.transform = 'translateX(' + toPx + 'px)';
      instance.track.style.willChange = ''; // clear the inline override so .is-dragging controls promotion again
      state.currentIndex = targetIndex;
      state.isAnimating = false;
      this.updateNavigation(instance);
    }

    updateNavigationState(instance) {
      const state = this.state.instances[instance.id];
      const productsPerView = this.getProductsPerView(instance);
      const maxIndex = Math.max(0, state.products.length - productsPerView);
      instance.prevButton.disabled = state.currentIndex <= 0;
      instance.nextButton.disabled = state.currentIndex >= maxIndex;
    }

    updateLayout(instance) {
      if (!instance.track) return;
      
      const productsPerView = this.getProductsPerView(instance);
      const isMobile = window.innerWidth < 750;

      // Add a helper class for the single-item mobile view to disable gap logic
      if (isMobile && productsPerView === 1) {
        instance.element.classList.add('lumistelle-carousel--single-item-mobile');
      } else {
        instance.element.classList.remove('lumistelle-carousel--single-item-mobile');
      }
      
      const productWidth = this.calculateProductWidth(instance);
      // Real flex gap (the grid mechanism): the track now carries column-gap,
      // so each tile shrinks by exactly the rendered gap. The per-slide step
      // (= productWidth) is unchanged, so all translate/drag/snap math stays
      // in sync. Reading the computed column-gap guarantees JS and CSS never
      // disagree (single-item-mobile sets it to 0 -> full-width cards).
      const trackGap = parseFloat(getComputedStyle(instance.track).columnGap) || 0;
      instance.track.querySelectorAll('.lumistelle-carousel__product').forEach(p => {
        p.style.width = `${Math.max(1, productWidth - trackGap)}px`;
      });
      
      this.setupDots(instance);
      this.slideTo(instance, this.state.instances[instance.id].currentIndex, false);
    }

    getProductsPerView(instance) {
        const width = window.innerWidth;
        const views = instance.config.productsPerView;

        if (width < 750) return views.mobile;
        if (width < 990) return views.tablet;
        if (width < 1921) return views.desktop; // Corrected breakpoint
        if (width < 2561) return views.large_desktop || views.desktop; // Corrected breakpoint
        return views.xl_desktop || views.large_desktop || views.desktop;
    }

    calculateProductWidth(instance) {
      if (!instance.viewport) {
        return 300; // Fallback width
      }

      const productsPerView = this.getProductsPerView(instance);
      
      // The viewport's offsetWidth now correctly reflects the width *after* the parent's padding is applied.
      // No extra subtractions are needed.
      const viewportWidth = instance.viewport.offsetWidth;

      // Gap-aware STEP (per-slide advance = card + column-gap). (viewport + gap) / perView
      // makes perView cards fill the viewport edge-to-edge like the 1fr grid, instead of
      // leaving a trailing gap that reveals the next card. updateLayout sets the visible
      // card width to (this - gap); all translate/bound/snap math uses this step, and the
      // physical flex layout step (card + gap) equals it, so paging stays in sync.
      const trackGap = parseFloat(getComputedStyle(instance.track).columnGap) || 0;
      return (viewportWidth + trackGap) / productsPerView;
    }

    _scheduleForHydration(swatchEl, productHandle, colorValue) {
      if (!this.hydrationQueue.has(productHandle)) {
        this.hydrationQueue.set(productHandle, []);
      }
      this.hydrationQueue.get(productHandle).push(swatchEl);
    }

    async _processHydrationQueue() {
      if (this.hydrationQueue.size === 0) return;

      const queue = new Map(this.hydrationQueue);
      this.hydrationQueue.clear();

      for (const [handle, swatchesToHydrate] of queue.entries()) {
        try {
          const response = await fetch(`/products/${handle}.js`);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status} for product: ${handle}`);
          }
          const fullProductData = await response.json();

          swatchesToHydrate.forEach(swatchEl => {
            const color = swatchEl.dataset.color;
            const variantWithImage = fullProductData.variants.find(v => v.option1 === color && v.featured_image);
            
            if (variantWithImage) {
              const createSwatchUrl = (src) => {
                if (!src) return '';
                try {
                  const [base, params] = src.split('?');
                  const pathParts = base.split('.');
                  const extension = pathParts.pop();
                  const pathWithoutExt = pathParts.join('.');
                  return `${pathWithoutExt}_100x125_crop_top.${extension}${params ? '?' + params : ''}`;
                } catch (e) { return src; }
              };

              const imageUrl = createSwatchUrl(variantWithImage.featured_image.src);
              const img = document.createElement('img');
              img.src = imageUrl;
              img.alt = color;
              img.loading = 'lazy';
              img.width = 50;
              img.height = 62;
              swatchEl.innerHTML = ''; // Clear placeholder
              swatchEl.appendChild(img);
              swatchEl.removeAttribute('data-needs-hydration');
            }
          });
        } catch (error) {
          console.error(`[Lumistelle Hydration Error] for handle "${handle}":`, error);
        }
      }
    }

    // ======================================================================
    // CLEANUP
    // ======================================================================

    destroyComplete(instanceId) {
      const instance = this.instances.get(instanceId);
      if (!instance) return;

      // ======================================================================
      // PHASE 4: COMPREHENSIVE OBSERVER CLEANUP
      // ======================================================================

      // Use central registry to disconnect all observers for this instance
      const observerCount = this.disconnectInstanceObservers(instanceId);

      // PHASE 4: Additional safeguard - check for any orphaned observers on instance
      // === PHASE 3: Added unifiedVisibilityObserver to the list ===
      const orphanedObservers = [
        'gridAutoplayObserver',
        'mobilePreloadObserver',
        'imageAutoplayObserver',
        'unifiedVisibilityObserver'
      ];

      orphanedObservers.forEach(observerProp => {
        if (instance[observerProp]) {
          try {
            if (typeof instance[observerProp].disconnect === 'function') {
              instance[observerProp].disconnect();
            }
            instance[observerProp] = null;
          } catch (error) {
          }
        }
      });

      // === PHASE 3: Clean up visible cards tracking for this instance ===
      if (this.visibleCards.has(instanceId)) {
        this.visibleCards.get(instanceId).clear();
        this.visibleCards.delete(instanceId);
      }

      // Cleanup preload observers
      this.cancelAllPreloading(instance);

      // Cleanup desktop preload listeners (already in registry, but double-check)
      if (instance.desktopPreloadHandlers && instance.track) {
        // These should be removed by removeInstanceEventListeners, but explicitly remove as safeguard
        try {
          instance.track.removeEventListener('mouseover', instance.desktopPreloadHandlers.handleMouseOver);
          instance.track.removeEventListener('mouseout', instance.desktopPreloadHandlers.handleMouseOut);
        } catch (e) {
        }
        instance.desktopPreloadHandlers = null;
      }

      // PHASE 3 HOT FIX: Clear preload setup flag
      instance.preloadListenersSetup = false;

      // --- END: REFACTORED CLEANUP LOGIC ---

      if (instance.gridAutoplayTimer) {
        clearInterval(instance.gridAutoplayTimer);
      }

      if (instance.imageAutoplayObserver) {
        instance.imageAutoplayObserver.disconnect();
      }

      // ======================================================================
      // PHASE 5: COMPREHENSIVE TIMER CLEANUP
      // ======================================================================

      // Use central registry to clear all timers for this instance
      const timerCount = this.clearInstanceTimers(instanceId);

      // PHASE 5: Additional safeguard - manually clear instance-level timer properties
      const instanceTimers = [
        { prop: 'autoplayInterval', type: 'autoplay' },
        { prop: 'gridAutoplayTimer', type: 'grid-autoplay' },
        { prop: 'preloadTimer', type: 'preload' }
      ];

      instanceTimers.forEach(({ prop, type }) => {
        if (instance[prop]) {
          clearInterval(instance[prop]);
          clearTimeout(instance[prop]);
          instance[prop] = null;
        }
      });

      // PHASE 5: Clear all element-level timers from product cards
      if (instance.track) {
        const products = instance.track.querySelectorAll('.lumistelle-carousel__product');
        let elementTimersCleared = 0;
        
        products.forEach(card => {
          // Clear image cycle timers
          if (card.imageCycleTimer) {
            clearInterval(card.imageCycleTimer);
            card.imageCycleTimer = null;
            elementTimersCleared++;
          }
          
          // Clear preload timers
          if (card.preloadTimer) {
            clearTimeout(card.preloadTimer);
            card.preloadTimer = null;
            elementTimersCleared++;
          }
        });
        
        if (elementTimersCleared > 0) {
        }
      }

      // PHASE 3 HOT FIX #2: Clear autoplay hover listener flag and handlers
      instance.autoplayHoverListenersSetup = false;
      instance.autoplayHoverHandlers = null;

      // PHASE 6: Clean up all DOM element references
      if (instance.element) {
        // Remove all product data references from child elements
        const products = instance.element.querySelectorAll('[data-product-id]');
        products.forEach(productEl => {
          // Clear dataset
          delete productEl.dataset.productId;
          delete productEl.dataset.index;
          
          // PHASE 6: element.productData is no longer used, but clean legacy references
          if (productEl.productData) {
            delete productEl.productData;
          }
          
          // Clear any stored image state
          if (productEl.currentImageIndex !== undefined) {
            delete productEl.currentImageIndex;
          }
          if (productEl.preloadedImageUrls) {
            productEl.preloadedImageUrls.clear();
            delete productEl.preloadedImageUrls;
          }
        });
        
        // PHASE 6: Remove from WeakMap
        this.elementToInstance.delete(instance.element);
        
        // Clear element content
        instance.element.innerHTML = '';
      }
      
      // PHASE 6: Remove from tracking
      this.instanceIds.delete(instanceId);
      
      // Standard cleanup
      this.instances.delete(instanceId);
      delete this.state.instances[instanceId];
      
      // PHASE 2: Remove any animation callbacks for this instance
      this.removeAnimationCallbacks(instanceId);

      // PHASE 1: Mark instance as inactive
      this._trackInstanceActivity(instanceId, false);

      // PHASE 3: Remove event listeners for this instance
      this.removeInstanceEventListeners(instanceId);
    }

    // ======================================================================
    // AJAX PAGINATION (NEW)
    // ======================================================================
    setupAjaxPagination(instance) {
      instance.paginationContainer = document.getElementById(`lumistelle-ajax-pagination-${instance.element.dataset.sectionId}`);
      instance.gridContainer = instance.element.querySelector('.lumistelle-grid__container');

      if (!instance.paginationContainer || !instance.gridContainer) return;

      const paginationClickHandler = this.handlePaginationClick.bind(this, instance);
      instance.paginationContainer.addEventListener('click', paginationClickHandler);
      this.registerEventListener(instance.id, instance.paginationContainer, 'click', paginationClickHandler);
    }

    handlePaginationClick(instance, event) {
      const target = event.target.closest('a, button');
      if (!target || instance.isLoading) return;

      event.preventDefault();

      // Corrected: Get URL from href for links, or data-next-url for buttons
      const url = target.href || target.dataset.nextUrl;
      
      if (url) {
        this.loadMoreProductsAJAX(instance, url);
      }
    }

    async loadMoreProductsAJAX(instance, url) {
      instance.isLoading = true;
      instance.paginationContainer.classList.add('is-loading');

      try {
        // === SCROLL PERF: Section Rendering API fast path ===
        // Fetch only this section instead of the full page — the JSON island,
        // cards, and pagination all live inside it. Falls back to the full
        // page if the section render fails for any reason.
        const sectionId = instance.element.dataset.sectionId;
        const sectionUrl = url + (url.includes('?') ? '&' : '?') + 'section_id=' + sectionId;

        let html = null;
        // Use the prefetched copy of this page if it's ready (see _prefetchNextPage).
        const prefetched = instance._prefetch;
        instance._prefetch = null;
        if (prefetched && prefetched.url === sectionUrl) html = await prefetched.promise;
        if (!html) {
          try {
            const fastResponse = await fetch(sectionUrl);
            if (fastResponse.ok) html = await fastResponse.text();
          } catch (e) { /* fall through to full-page fetch */ }
        }

        const parser = new DOMParser();
        let newDoc = html ? parser.parseFromString(html, 'text/html') : null;

        // Fallback guard: wrap behavior, not just presence — if the section
        // render came back without a grid, refetch the full page.
        if (!newDoc || !newDoc.querySelector('.lumistelle-grid__container')) {
          if (newDoc) console.warn('[Lumistelle AJAX] Section render unusable — falling back to full page fetch');
          const response = await fetch(url);
          if (!response.ok) throw new Error(`HTTP error ${response.status}`);
          newDoc = parser.parseFromString(await response.text(), 'text/html');
        }

        // --- START: THE CRITICAL FIX ---
        // 1. Find the new JSON data island in the fetched HTML.
        const newProductDataElement = newDoc.getElementById(`lumistelle-collection-products-json-${instance.element.dataset.sectionId}`);
        if (newProductDataElement) {
          const newProductsData = JSON.parse(newProductDataElement.textContent);
          
          // 2. Add the new, complete product data to the engine's central memory.
          newProductsData.forEach(productData => {
            if (!this.variantData.has(productData.id)) {
              this.variantData.set(productData.id, productData);
            }
          });
        } else {
        }
        // --- END: THE CRITICAL FIX ---

        const paginationStyle = instance.paginationContainer.dataset.paginationStyle;
        if (paginationStyle === 'pagination') {
          instance.gridContainer.innerHTML = '';
          // Scroll to the top of the grid container, not the whole section
          instance.gridContainer.scrollIntoView({ behavior: 'smooth' });
        }

        // Extract and append new product elements
        // === SCROLL PERF: chunked append + hydration ===
        // Appending and hydrating the whole batch in one pass forces one giant
        // style/layout frame. Cards arrive below the viewport (the trigger's
        // rootMargin head start), so staging them is invisible to the user.
        // Two cards per frame (QA, October 2026): six per frame cost a
        // 160ms frame at 4x CPU, and the styling, layout, paint and Clarity
        // work all scale with the cards added at once.
        // isLoading stays true for the whole loop, so the observer can't
        // double-fire mid-append.
        const newProductElements = Array.from(newDoc.querySelectorAll('.lumistelle-grid__container .lumistelle-carousel__product'));
        const newElementsToAnimate = [];
        const CHUNK_SIZE = 2;
        // Two cards per frame, each added just after a frame so its styling lands
        // in the next one. Waiting for idle time instead (tried October 2026)
        // stalled every step during a fast scroll: there is no idle time then, so
        // a 12-card batch took 0.6s to arrive and flings kept hitting the bottom.
        const nextSlot = () => new Promise(r => requestAnimationFrame(() => setTimeout(r, 0)));

        // Wait for the first slot after the parse, so fetch/parse and the first
        // append never share a frame.
        await nextSlot();

        for (let i = 0; i < newProductElements.length; i += CHUNK_SIZE) {
          // The grid was replaced or removed mid-batch: stop adding to it.
          if (!instance.gridContainer.isConnected) break;
          const chunk = newProductElements.slice(i, i + CHUNK_SIZE);
          const fragment = document.createDocumentFragment();
          chunk.forEach(product => fragment.appendChild(product));
          instance.gridContainer.appendChild(fragment);

          chunk.forEach((cardEl, j) => {
            if (cardEl.hasAttribute('data-hydrated')) return;
            const productId = parseInt(cardEl.dataset.productId, 10);
            const productData = this.variantData.get(productId);
            if (productData) {
              this.updateProductElement(cardEl, productData, i + j, instance.config);
              cardEl.setAttribute('data-hydrated', 'true');
            }
          });

          newElementsToAnimate.push(...chunk);
          if (i + CHUNK_SIZE < newProductElements.length) await nextSlot();
        }
        this.debouncedProcessHydration();
        if (window.LumistelleCarouselAnimations && typeof window.LumistelleCarouselAnimations.observeNewProducts === 'function') {
          window.LumistelleCarouselAnimations.observeNewProducts(instance, newElementsToAnimate);
        }

        // The unified observer replaced three separate ones; this call site was
        // never renamed, so it read an undefined property and silently skipped
        // every appended card. Old name kept as a fallback in case any path
        // still sets it.
        const visibilityObserver = instance.unifiedVisibilityObserver || instance.gridAutoplayObserver;
        if (visibilityObserver) {
          newElementsToAnimate.forEach(card => visibilityObserver.observe(card));
        }

        document.dispatchEvent(new CustomEvent('lumistelle:cards:rendered'));

        // Update pagination controls
        const newPagination = newDoc.getElementById(`lumistelle-ajax-pagination-${instance.element.dataset.sectionId}`);
        if (newPagination) {
          instance.paginationContainer.innerHTML = newPagination.innerHTML;
        } else {
          instance.paginationContainer.remove();
        }

        // Start fetching the page after this one in idle time.
        this._prefetchNextPage(instance);

        // Update browser URL for standard pagination
        if (paginationStyle === 'pagination') {
          window.history.pushState({ path: url }, '', url);
        }

      } catch (error) {
        console.error('[Lumistelle AJAX Error] Failed to load more products:', error);
        window.location.href = url; 
      } finally {
        instance.isLoading = false;
        if (instance.paginationContainer) {
          instance.paginationContainer.classList.remove('is-loading');
        }
      }
    }


    // Fetch the next page's grid HTML in idle time, so the infinite-scroll
    // trigger only has to add the cards, with no network wait (QA, October
    // 2026). Infinite-scroll grids only; HTML only, no images.
    _prefetchNextPage(instance) {
      const container = instance.paginationContainer;
      if (!container || !container.isConnected) return;
      if (container.dataset.paginationStyle !== 'infinite') return;
      const start = () => {
        if (instance.isLoading || !container.isConnected) return;
        const trigger = container.querySelector('[data-next-url]');
        const nextUrl = trigger && trigger.dataset.nextUrl;
        if (!nextUrl) return;
        const sectionUrl = nextUrl + (nextUrl.includes('?') ? '&' : '?') + 'section_id=' + instance.element.dataset.sectionId;
        if (instance._prefetch && instance._prefetch.url === sectionUrl) return;
        instance._prefetch = {
          url: sectionUrl,
          promise: fetch(sectionUrl).then(r => (r.ok ? r.text() : null)).catch(() => null)
        };
      };
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(start, { timeout: 2000 });
      } else {
        setTimeout(start, 300);
      }
    }

    // ======================================================================
    // PHASE 2: SMART ANIMATION LOOP MANAGEMENT
    // ======================================================================

    /**
     * Initialize animation loop system (but don't start it yet)
     * Loop will start automatically when first callback is added
     */
    setupAnimationLoop() {
      // Create the animation function that will be called recursively
      this._animationLoopFunction = () => {
        // Only continue if loop should be running
        if (!this.rafIsRunning) {
          this.rafId = null;
          return;
        }
        
        // Execute all registered callbacks
        if (this.rafCallbacks.size > 0) {
          this.rafCallbacks.forEach(cb => {
            try {
              cb();
            } catch (error) {
              console.error('[Lumistelle Animation] Callback error:', error);
            }
          });
        }
        
        // Schedule next frame
        this.rafId = requestAnimationFrame(this._animationLoopFunction);
      };
    }

    /**
     * Start the animation loop
     * Only starts if not already running and callbacks exist
     */
    startAnimationLoop() {
      // Don't start if already running
      if (this.rafIsRunning) {
        return;
      }
      
      // Don't start if no callbacks registered
      if (this.rafCallbacks.size === 0) {
        return;
      }
      
      // Don't start if page is hidden
      if (!this.isPageVisible) {
        return;
      }
      
      this.rafIsRunning = true;
      this.rafLastStartTime = Date.now();
      this.rafId = requestAnimationFrame(this._animationLoopFunction);
    }

    /**
     * Stop the animation loop
     * Cancels the current frame and prevents further scheduling
     */
    stopAnimationLoop() {
      if (!this.rafIsRunning) {
        return; // Already stopped
      }
      
      this.rafIsRunning = false;
      this.rafLastStopTime = Date.now();
      
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      
      const runTimeMs = this.rafLastStartTime ? this.rafLastStopTime - this.rafLastStartTime : 0;
    }

    /**
     * Check if animation loop is currently running
     * @returns {boolean} True if loop is active
     */
    isAnimationLoopRunning() {
      return this.rafIsRunning;
    }

    /**
     * Add a callback to the animation loop
     * Automatically starts the loop if not running
     * @param {string} instanceId - The instance identifier
     * @param {Function} callback - The animation callback function
     */
    addAnimationCallback(instanceId, callback) {
      if (!instanceId || typeof callback !== 'function') {
        console.error('[Lumistelle Animation] Invalid callback registration:', { instanceId, callback });
        return;
      }
      
      // Store callback with instance ID for later removal
      const callbackData = { instanceId, callback };
      this.rafCallbacks.add(callbackData);
      
      // Start loop if this is the first callback and page is visible
      if (this.rafCallbacks.size === 1 && this.isPageVisible) {
        this.startAnimationLoop();
      }
    }

    /**
     * Remove all callbacks for a specific instance
     * Automatically stops the loop if no callbacks remain
     * @param {string} instanceId - The instance identifier
     * @returns {number} Number of callbacks removed
     */
    removeAnimationCallbacks(instanceId) {
      if (!instanceId) {
        return 0;
      }
      
      let count = 0;
      const callbacksToRemove = [];
      
      // Find all callbacks for this instance
      this.rafCallbacks.forEach(callbackData => {
        if (callbackData.instanceId === instanceId) {
          callbacksToRemove.push(callbackData);
          count++;
        }
      });
      
      // Remove them
      callbacksToRemove.forEach(cb => this.rafCallbacks.delete(cb));
      
      if (count > 0) {
      }
      
      // Stop loop if no callbacks remain
      if (this.rafCallbacks.size === 0) {
        this.stopAnimationLoop();
      }
      
      return count;
    }

    /**
     * Check if there are any active animation callbacks
     * @returns {boolean} True if callbacks are registered
     */
    hasActiveCallbacks() {
      return this.rafCallbacks.size > 0;
    }

    // Other methods...
    handleError(context, error) { console.error(`[LumistelleCarousel] Error in ${context}:`, error); }
    mark(name) { if (window.performance?.mark) window.performance.mark(name); }

  }

  // ======================================================================
  // CAROUSEL INSTANCE CLASS
  // ======================================================================
  class CarouselInstance {
    constructor(id, element, config, engine) {
      this.id = id;
      this.element = element;
      this.config = { ...engine.config, ...config };
      this.engine = engine;
      this.gridAutoplayTimer = null;
      // Mouse Drag State
      this.isMouseDown = false;
      this.startX = 0;
      this.dragStartTranslate = 0;
      this.rafId = null;
      this.hasDragged = false;
      // AJAX Pagination State
      this.paginationContainer = null;
      this.gridContainer = null;
      this.isLoading = false;
    }
    slideTo(index, animate) { this.engine.slideTo(this, index, animate); }
    slideNext() { this.engine.slideNext(this); }
    slidePrev() { this.engine.slidePrev(this); }
    destroy() { this.engine.destroyComplete(this.id); }
  }

// ======================================================================
// EXPORT CLASS DEFINITION
// ======================================================================
window.LumistelleCarouselEngine = LumistelleCarouselEngine;
window.LumistelleCarouselInstance = CarouselInstance;
})(window, document);